'use client';

import React, { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { buildScopeFilter, buildTimelineCopySourceContext, extractPathAfterBulk, fileInfoFromProvenanceBody, folderPathForEvidenceLockerDisplay, getCaseDisplayName, handleDownload, isContainerLikeSource, isUfdrRootSource, isZipExportRoot, pathForAssistantScope, resolveFileInfoMediaDisplayUrl, stripUnwantedLeadingPathSegments, toProvenanceLookupPath } from '@/shared/functions';
import {
  buildEvidenceLockerPreviewSource,
  evidenceFileTypeLabel,
  fileSummaryFromTreeNode,
  toBypassCmPreviewSource,
} from './evidence-locker-ui-shared-new';
import { useCaseFolderName } from '@/hooks/useCaseFolderName';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { useMediaPlayerTransitionEnabled } from '@/hooks/useMediaPlayerTransition';
import { useDismissOnViewExit } from '@/hooks/useDismissOnViewExit';
import { MEDIA_PREVIEW_OVERLAY_SURFACE_CLASS } from '@/shared/inverse-overlay-surfaces';
import {
  FunnelIcon,
  FunnelXIcon,
  ListMagnifyingGlassIcon,
  XCircleIcon,
  FolderOpenIcon,
  LockersIcon,
  XIcon,
  CheckIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  ArrowClockwiseIcon,
  FilesIcon,
  EyeSlashIcon,
  MagnifyingGlassPlusIcon,
  LightbulbFilamentIcon, CrosshairIcon, DeviceMobileIcon,
  MagnifyingGlassIcon,
  CaretDownIcon,
} from "@phosphor-icons/react";
import { useHotkeys } from 'react-hotkeys-hook';
import {
  formatProvenanceStatusLabel,
  getProvenanceIconDisplaySize,
  getProvenanceToolbarIconClassNames,
  isStatus,
  useWindowSize,
} from '../shared/clientFunctions';
import { ProvenanceIcon } from '@/shared/provenance-icon';
import { motion } from 'framer-motion';
import { QUESTION_BAR_ICON_BUTTON_CLASS, QUESTION_BAR_ICON_BUTTON_BACKGROUND, QUESTION_BAR_SUBMIT_RINGLESS_CLASS } from './question-bar-ui';
import './question-bar.css';
import { AuthContext, FeaturesContext, FullScreenContext, StatsContext } from '../app/contexts';
import { useCsamAccess } from '@/lib/csam-access';
import { usePersonalFilesCaseId } from '@/hooks/usePersonalFilesCaseId';
import { isMobileAnalysisAvailableForCase } from '@/lib/mobile-analysis-agent';
import {
  buildQuestionSubmitScopes,
  caseFolderNameForChatScopeTag,
  mapScopeTagsToFolderNames,
} from './build-question-submit-scopes';
import { Button } from "@tranquility-ai/theme-components/button";
import { Modal } from '@tranquility-ai/theme-components/modal';
import '@tranquility-ai/theme-components/modal.css';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import { recordStat, removeMD5Hash } from '@/shared/functions';
import { useDebounce } from 'use-debounce';
import { MediaSource, ScopesObject, Source } from '@/shared/types';
import { fileTypeControllers, META_FILE_REGEX, emailViewerS3KeyFromUrl } from '@/shared/constants';
import { useEmailArchivePreview } from '@/components/email-viewer/EmailArchivePreviewProvider';
import { mailboxPreviewTarget } from '@/components/email-viewer/mailbox-preview-target';
import { fileTypeIcons } from '@/shared/uiConstants';
import { Popover, PopoverTrigger, PopoverContent } from '@heroui/react';
import { SourceCard } from './sourceCard';
import { CsamDetectionPill } from './ui/media/csam-detection-pill';
import { ContentModerationAckModal } from './ui/content-moderation-ack-modal';
import { type UfdrPreviewContext } from '@/hooks/useUfdrExportData';
import { consumeUfdrViewerHandoff, ufdrPreviewFromRequest } from '@/shared/mobile-service/open-ufdr-viewer';
import { EvidenceLockerMobileForensicOverlay } from './evidence-locker-mobile-forensic-overlay';
import { useMediaSourceShare } from './mediaSourceShare';
import { EventFromChat } from './answer-item';
// Typechecks once @tranquility-ai/theme-components 3.9.0 is pinned.
import { Checkbox } from '@tranquility-ai/theme-components';
import { isPromptFlagOn } from '@/i18n/promptCatalog';
import { PromptLibrary } from './prompt-library';
import { useCasesList } from '@/hooks/useCasesList';
import {
  caseFolderLabels,
  relabelCaseFolderSegments,
  resolveCaseScope,
} from '@/shared/case-summary';
import { useProvenanceFiles } from '@/hooks/useProvenanceFiles';
import type { FileSummary, ProvenanceFileInfoBody, ProvenanceFilesFilters, TreeNodeResponse } from '@/shared/cors-provenance-api';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import EvidenceLockerTree, { type EvidenceLockerTreeHandle } from './evidence-locker-tree';
import { EvidenceLockerModalNew } from './evidence-locker-modal-new';
import { DismissibleBlurOverlay } from './dismissible-blur-overlay';
import {
  EvidenceFilterPickerNew,
  hasEvidenceFilterActive,
} from './evidence-filter-picker-new';
import { shouldUseOldChatNewEvidenceLocker } from './new-evidence-locker-flags';
import { evidenceCategoryScopeFieldsWithWireTokens, toggleEvidenceCategoryScopeInclude, FilterButton } from './evidence-locker-ui-shared-new';

interface EvidenceObject {
  mediaType: string;
  url: string;
  /** Total page count for the underlying document, when known from FileSummary. */
  pageCount?: number | null;
  /** Preview URL for the file (e.g. a processed/thumbnail version), used as previewFilePath in SourceCard. */
  previewUrl?: string | null;
  /** Content-moderation detection type (e.g. "CSAM") — present means file is moderated. */
  contentModerationType?: string | null;
}

/** Chip row labels (PDF, …) → provenance ``fileTypes`` tokens; ScopeDropdown categories are not used for listing. */
const EVIDENCE_LISTING_TYPE_TOKEN_BY_UI_KEY: Record<string, string> = {
  PDF: 'pdf',
  'MS Office': 'office',
  Image: 'image',
  Video: 'video',
  Audio: 'audio',
  Spreadsheet: 'spreadsheet',
  Email: 'email',
  Other: 'other',
};

function fileTypesTokensForEvidenceListing(uiKeys: string[]): string[] {
  return uiKeys.map((k) => EVIDENCE_LISTING_TYPE_TOKEN_BY_UI_KEY[k] ?? k.toLowerCase());
}

const getFileTypeIconsControllersIconsMap = () => {
  const fileTypeIconsControllers = Object.entries(fileTypeControllers);
  const iconMap: Record<string, React.ElementType> = {};
  fileTypeIconsControllers.forEach((fileTypeIconsController) => {
    const iconType = fileTypeIconsController[0];
    fileTypeIconsController[1].forEach((fileType) => {
      iconMap[fileType] = fileTypeIcons[iconType as keyof typeof fileTypeIcons];
    });
  });
  return iconMap;
}

export interface QuestionBarProps {
  answers: any[];
  caseId: string;
  userId: string;
  sessionId: string | null;
  onSubmit: (
    query: string,
    scopesAndFiles: ScopesObject,
    userId: string,
    sessionId: string | null,
    useDeepDive: boolean,
    useMobile?: boolean,
  ) => Promise<void>;
  onClearThread: () => void;
  isLoading?: boolean;
  isResetting?: boolean;
  setQuery: (query: string) => void;
  query: string;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  hasChatHistory: boolean;
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  /** While true, keep expanded layout + textarea content frozen until the new AnswerItem mounts. */
  holdSubmitVisualLock?: boolean;
  collapseSources: () => void;
  scopesAndFiles: ScopesObject;
  setScopesAndFiles: (scopesAndFiles: ScopesObject | ((prev: ScopesObject) => ScopesObject)) => void;
  showFolders: boolean;
  setShowFolders: React.Dispatch<React.SetStateAction<boolean>>;
  allFileTypes: boolean;
  setAllFileTypes: React.Dispatch<React.SetStateAction<boolean>>;
  activeFileTypes: Record<string, boolean>;
  setActiveFileTypes: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  evidenceFilter: string;
  addSourceToScope: (path: string) => void;
  addToTimeline: (event: EventFromChat) => void;
  featureFlagsProp?: { [key: string]: any };
  triggerPromptLibrary?: number;
  /** User cleared evidence via FunnelX while expanded (skip auto-restore from last answer when returning to Assistant). */
  onAssistantEvidenceFilterCleared?: () => void;
  /** When true, funnel opens ``EvidenceLockerModalNew`` instead of the inline locker. */
  useNewEvidenceLockerModal?: boolean;
  /** False once the user navigates to another top-nav view; closes portaled locker overlays. */
  assistantViewSelected?: boolean;
  /** False when Assistant is hidden (e.g. CSAM Dashboard). Dismiss the locker overlay. */
  assistantViewVisible?: boolean;
}

const DynamicIcon = (
  {
    icon: Icon,
    size,
    weight,
    className
  }: {
    icon: React.ElementType,
    size: number,
    weight: string,
    className: string
  }
) => {
  return <Icon size={size} weight={weight} className={className} />;
};

const ScopeDropdown = ({
  selectedContentCategories,
  onToggleCategory,
}: {
  selectedContentCategories: string[];
  onToggleCategory: (category: string) => void;
}) => {
  const t = useTimePilotT();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const allTypes = Object.keys(fileTypeControllers);
  const selectedCount = selectedContentCategories.length;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className="relative shrink-0">
      <Button
        type="button"
        style="outline"
        color="accent"
        background="default"
        size="sm"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`flex items-center justify-between gap-2 pl-4 pr-2 py-2 rounded-lg border-[2px] border-solid border-brand-blue-light cursor-pointer w-[184px] transition-colors duration-200 ${isOpen ? 'bg-brand-blue-light/20' : 'bg-transparent hover:bg-brand-blue-light/10'}`}
      >
        <span className="text-[14px] text-black whitespace-nowrap tracking-[-0.42px]">
          {selectedCount > 0
            ? t(selectedCount === 1
              ? 'chat.evidence.filters.fileTypeCount'
              : 'chat.evidence.filters.fileTypeCountPlural', { count: selectedCount })
            : t('chat.evidence.filters.scopeByFileTypes')}
        </span>
        <CaretDownIcon size={20} weight="regular" className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </Button>
      {isOpen && (
        <div className="absolute top-full mt-1 right-0 z-[60] bg-white border-[2px] border-solid border-brand-blue-light rounded-md shadow-lg w-[184px] max-h-[240px] overflow-y-auto custom-scrollbar3">
          {allTypes.map((type) => {
            const Icon = fileTypeIcons[type as keyof typeof fileTypeIcons];
            return (
              <div
                key={type}
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-brand-blue-light/10 transition-colors duration-150"
                onClick={() => onToggleCategory(type)}
              >
                <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    className="min-w-[18px] max-w-[18px] min-h-[18px] max-h-[18px]"
                    checked={selectedContentCategories.includes(type)}
                    onCheckedChange={() => onToggleCategory(type)}
                  />
                </span>
                {Icon && <Icon size={18} weight="light" className="text-gray-500" />}
                <span className="text-[13px] text-black/80">{type}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const getNewActiveFileTypes = (fileType: string, prev: Record<string, boolean>) => {
  const newActiveFileTypes = { ...prev };
  newActiveFileTypes[fileType] = !prev[fileType];
  return newActiveFileTypes;
};

export const isFilterTooBig = (filterSizeLimit: number, scopesAndFiles: ScopesObject, caseId: string) => {
  if (filterSizeLimit === 0) {
    return false;
  }
  const filter = buildScopeFilter(scopesAndFiles.scopes, scopesAndFiles.files, scopesAndFiles.excludedFiles, caseId, 'case_id');
  const filterString = JSON.stringify(filter);
  const filterSize = filterString ? filterString.length : 0;
  if (filterSize > filterSizeLimit) {
    return true;
  }
  return false;
}

export const QuestionBar: React.FC<QuestionBarProps> = ({
  caseId,
  onSubmit,
  userId,
  sessionId,
  onClearThread,
  isLoading = false,
  isResetting,
  setQuery,
  query,
  textareaRef,
  hasChatHistory,
  isExpanded,
  setIsExpanded,
  holdSubmitVisualLock = false,
  collapseSources,
  scopesAndFiles,
  setScopesAndFiles,
  showFolders,
  setShowFolders,
  allFileTypes,
  setAllFileTypes,
  activeFileTypes,
  setActiveFileTypes,
  evidenceFilter,
  addSourceToScope,
  addToTimeline,
  featureFlagsProp,
  triggerPromptLibrary,
  onAssistantEvidenceFilterCleared,
  useNewEvidenceLockerModal: useNewEvidenceLockerModalProp,
  assistantViewSelected = true,
  assistantViewVisible = true,
}) => {
  const t = useTimePilotT();
  const ufdrCaseFolderName = useCaseFolderName(caseId);
  const themeEmailArchive = useMediaPlayerTransitionEnabled('emailArchive');
  const emailArchivePreview = useEmailArchivePreview();
  const { width } = useWindowSize();
  const isSmallScreen = width !== undefined && width < 450;

  const [isAlertOpen, setIsAlertOpen] = useState(isResetting ?? false);
  const selectedItemsRef = useRef<HTMLDivElement>(null);
  const featuresContext = useContext(FeaturesContext);
  const statsContext = useContext(StatsContext);
  const fullScreenContext = useContext(FullScreenContext);
  const mediaSourceShare = useMediaSourceShare();
  const { cellId: downloadCellId, accessToken: downloadAccessToken } = useCellServiceSession();

  // Safely extract values with fallbacks
  const featureFlags = featuresContext?.featureFlags || {};
  const personalFilesCaseId = usePersonalFilesCaseId();
  const mobileAnalysisAgentEnabled = isMobileAnalysisAvailableForCase(
    featureFlagsProp ?? featureFlags,
    caseId,
    personalFilesCaseId,
  );
  const handleDownloadWithIntercomFlag = useCallback(
    (sourceObject: MediaSource | Source) => {
      const session = downloadCellId && downloadAccessToken
        ? { cellId: downloadCellId, accessToken: downloadAccessToken }
        : undefined;
      handleDownload(sourceObject, session);
    },
    [downloadCellId, downloadAccessToken],
  );
  const contentCategoriesEnabled = !!featureFlags?.contentCategories;
  // Master kill-switch for CSAM features (label, obfuscated preview, ack gate).
  // When off, `availableEvidence` carries no contentModerationType, so every CSAM render site here
  // (which all read from `availableEvidence`) treats sources as non-CSAM.
  const { canSeeCsamTags, canRevealOriginal } = useCsamAccess();
  const themeUfdrEnabled = useMediaPlayerTransitionEnabled('ufdr');
  const { isFullscreen, setIsFullscreen, fullscreenMediaItem, setFullscreenMediaItem, linkedMediaItem, setLinkedMediaItem } = fullScreenContext || {};

  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [availableEvidence, setAvailableEvidence] = useState<EvidenceObject[]>([]);
  const [allEvidence, setAllEvidence] = useState<EvidenceObject[]>([]);
  const [selectedEvidence, setSelectedEvidence] = useState<string[]>([]);
  const [excludedEvidence, setExcludedEvidence] = useState<string[]>([]);
  const [filesInputValue, setEvidenceInputValue] = useState<string>('');
  const [selectedEvidenceTypes, setSelectedEvidenceTypes] = useState<string[]>([]);
  // Derived from scopesAndFiles to keep a single source of truth; a prior
  // implementation duplicated this into local state with two-way sync, which
  // caused an oscillation loop whenever the parent reset scopesAndFiles
  // (case switch, chat-history load) while local state still held the
  // pre-reset value. ``useMemo`` keeps the reference stable across renders
  // when the underlying array is unchanged so downstream memos / effects do
  // not re-run on every parent re-render.
  const selectedContentCategories = useMemo(
    () => scopesAndFiles?.evidenceCategoryScopes ?? [],
    [scopesAndFiles?.evidenceCategoryScopes],
  );

  const excludedContentCategories = useMemo(
    () => scopesAndFiles?.excludedEvidenceCategoryScopes ?? [],
    [scopesAndFiles?.excludedEvidenceCategoryScopes],
  );

  const [loadingScopes, setLoadingScopes] = useState(false);
  const [availableScopes, setAvailableScopes] = useState<string[]>([]);
  const [allScopes, setAllScopes] = useState<string[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [excludedScopes, setExcludedScopes] = useState<string[]>([]);
  const [scopeInputValue, setScopeInputValue] = useState<string>('');
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [isEvidenceLockerModalOpen, setIsEvidenceLockerModalOpen] = useState(false);
  const [filterPickerOpen, setFilterPickerOpen] = useState(false);
  const useNewEvidenceLockerModal =
    useNewEvidenceLockerModalProp ??
    shouldUseOldChatNewEvidenceLocker(featureFlags);
  const lockerUiOpen = useNewEvidenceLockerModal
    ? isEvidenceLockerModalOpen
    : isFilterVisible;

  const openEvidenceLocker = useCallback(() => {
    setIsExpanded(true);
    if (useNewEvidenceLockerModal) {
      setIsEvidenceLockerModalOpen(true);
    } else {
      setIsFilterVisible(true);
    }
  }, [useNewEvidenceLockerModal, setIsExpanded]);
  const [copiedSource, setCopiedSource] = useState<number>(-1);
  const [addedSource, setAddedSource] = useState<number>(-1);

  const [localIsLoading, setLocalIsLoading] = useState(false);
  const [localQuery, setLocalQuery] = useState<string>(query);
  const [hasText, setHasText] = useState(false);
  const [isFullyExpanded, setIsFullyExpanded] = useState(false);
  const [isBarTransitioning, setIsBarTransitioning] = useState(false);
  const BAR_LAYOUT_TRANSITION_MS = 500;
  const [isPromptLibraryOpen, setIsPromptLibraryOpen] = useState(false);
  /** Blocks tooltip re-show after modal close until the user hovers the trigger again. */
  const [suppressPromptLibraryTooltip, setSuppressPromptLibraryTooltip] = useState(false);
  const [previewPopoverOpen, setPreviewPopoverOpen] = useState<number>(-1);
  // URL-keyed preview state for rows that don't live in ``availableEvidence``
  // (subdocuments + virtual entries surfaced by the tree picker). Kept
  // alongside the legacy index-based ``previewPopoverOpen`` so existing
  // file-row preview behavior is untouched.
  const [previewPopoverUrl, setPreviewPopoverUrl] = useState<string | null>(null);
  const [ufdrPreviewContext, setUfdrPreviewContext] = useState<UfdrPreviewContext | null>(null);
  const raisePreviewAboveModal = !!ufdrPreviewContext;

  const themeUfdrEnabledRef = useRef(themeUfdrEnabled);
  themeUfdrEnabledRef.current = themeUfdrEnabled;
  useEffect(() => {
    consumeUfdrViewerHandoff({
      themeEnabled: themeUfdrEnabledRef.current,
      navigate: (href) => {
        window.location.assign(href);
      },
      openOverlay: (request) => {
        setUfdrPreviewContext(ufdrPreviewFromRequest(request));
      },
    });
  }, []);
  // Per-row preview-icon hover state. Keyed by URL (not the legacy ``index``)
  // because tree-only rows (subdocuments, virtual nodes) share ``index === -1``
  // and would otherwise all "highlight" together when any one is hovered.
  const [hoveredIconKey, setHoveredIconKey] = useState<string | null>(null);
  const [contentModerationAckIndex, setContentModerationAckIndex] = useState<number | null>(null);
  // URL-keyed acknowledgement for tree-only rows (deeply nested files /
  // subdocuments) that share ``index === -1`` in the flat ``availableEvidence``
  // list and would otherwise all open the ack modal at once.
  const [contentModerationAckUrl, setContentModerationAckUrl] = useState<string | null>(null);

  useEffect(() => {
    // Fullscreen media temporarily CSS-hides Assistant and therefore sets
    // assistantViewVisible to false. The Evidence Locker must survive that
    // handoff so minimizing its file preview restores the selected file,
    // folder, and search state instead of returning to an empty Assistant.
    if (assistantViewVisible || fullScreenContext.isFullscreen) return;
    setIsEvidenceLockerModalOpen(false);
    setIsFilterVisible(false);
    setPreviewPopoverOpen(-1);
    setPreviewPopoverUrl(null);
    setContentModerationAckIndex(null);
    setContentModerationAckUrl(null);
  }, [assistantViewVisible, fullScreenContext.isFullscreen]);

  const [showObfuscatedPreview, setShowObfuscatedPreview] = useState(true);
  // Non-reviewers can never leave the obfuscated view.
  const effectiveShowObfuscated = canRevealOriginal ? showObfuscatedPreview : true;
  const [filterIsTooBig, setFilterIsTooBig] = useState(false);
  const [strandsModeQuerySent, setStrandsModeQuerySent] = useState(false);
  const [mobileModeQuerySent, setMobileModeQuerySent] = useState(false);

  const [filesSearchTerm] = useDebounce(filesInputValue, 500);
  const [scopeSearchTerm] = useDebounce(scopeInputValue, 500);

  // Mirror the chip row + All Files state into the persisted scopes/files
  // payload. ``evidenceCategoryScopes`` is NOT written here: it is the source
  // of truth for ScopeDropdown selections and is written directly by the
  // dropdown / remove / clear handlers. Mixing both writes in a single effect
  // (with a stale local copy of ``evidenceCategoryScopes``) previously caused
  // an oscillation loop with a complementary sync effect.
  //
  // CRITICAL: derive ``scopeCats`` from ``prev`` inside the updater rather
  // than from the render-time closure. On case-switch renders other queued
  // state updates (case-change reset below, parent case-switch effect) reset
  // ``evidenceCategoryScopes`` to ``[]`` before this updater runs, but the
  // closure still holds the prior case's value. Using the closure copy here
  // caused this effect to write the prior case's chip selection onto the
  // empty ``prev``, persisting that wrong value into the new case's
  // localStorage via the parent's `handleSetScopesAndFiles`.
  useEffect(() => {
    if (!contentCategoriesEnabled) return;
    setScopesAndFiles((prev) => {
      const includedScopeCats = prev?.evidenceCategoryScopes ?? [];
      const excludedScopeCats = prev?.excludedEvidenceCategoryScopes ?? [];
      const synced = evidenceCategoryScopeFieldsWithWireTokens(
        includedScopeCats,
        excludedScopeCats,
        !!prev.evidenceAllFileTypes,
      );
      if (
        JSON.stringify(prev.contentCategories ?? []) ===
          JSON.stringify(synced.contentCategories) &&
        JSON.stringify(prev.excludedContentCategories ?? []) ===
          JSON.stringify(synced.excludedContentCategories) &&
        JSON.stringify(prev.evidenceCategoryChips ?? []) ===
          JSON.stringify(synced.evidenceCategoryChips) &&
        JSON.stringify(prev.evidenceCategoryScopes ?? []) ===
          JSON.stringify(synced.evidenceCategoryScopes) &&
        JSON.stringify(prev.excludedEvidenceCategoryScopes ?? []) ===
          JSON.stringify(synced.excludedEvidenceCategoryScopes) &&
        prev.evidenceAllFileTypes === synced.evidenceAllFileTypes
      ) {
        return prev;
      }
      return {
        ...prev,
        ...synced,
      };
    });
  }, [
    contentCategoriesEnabled,
    scopesAndFiles?.evidenceCategoryScopes,
    scopesAndFiles?.excludedEvidenceCategoryScopes,
    setScopesAndFiles,
  ]);

  /** Folder mode defers loading files until chips, "All Files", and/or ScopeDropdown categories apply. */
  const shouldLoadEvidenceFiles = useMemo(() => {
    const activeCategoryKeys = Object.keys(activeFileTypes).filter(
      (key) => activeFileTypes[key] === true,
    );
    return !showFolders || allFileTypes || activeCategoryKeys.length > 0;
  }, [showFolders, allFileTypes, activeFileTypes]);

  const formRef = useRef<HTMLFormElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const evidenceLockerRef = useRef<HTMLDivElement>(null);
  const evidenceLockerContentRef = useRef<HTMLDivElement>(null);
  const filterBarRowRef = useRef<HTMLDivElement>(null);
  const promptLibraryRef = useRef<HTMLDivElement>(null);
  const evidenceTreeRef = useRef<EvidenceLockerTreeHandle>(null);
  const addSourcesButtonRef = useRef<HTMLDivElement>(null);
  const assistantQueryButtonRef = useRef<HTMLButtonElement>(null);
  const previewPopoverRef = useRef<HTMLDivElement>(null);
  /** Evidence preview open before fullscreen (index and/or URL-keyed). */
  const popoverStateRef = useRef<{ index: number; url: string | null } | null>(null);
  const prevIsFullscreenRef = useRef(isFullscreen);
  const previewPopoverOpenRef = useRef(previewPopoverOpen);
  previewPopoverOpenRef.current = previewPopoverOpen;
  const previewPopoverUrlRef = useRef(previewPopoverUrl);
  previewPopoverUrlRef.current = previewPopoverUrl;
  const caseIdRef = useRef<string | null>(null);
  const shouldFocusTextareaAfterExpandRef = useRef(false);

  const iconMap = useMemo(() => getFileTypeIconsControllersIconsMap(), []);
  const { idToken, accessToken, tenantName } = useContext(AuthContext);
  const casesQuery = useCasesList();

  /**
   * Resolve the provenance scope for the currently selected case name.
   * - ``all-cases`` for empty / All Cases (``'*'``) → no ``tags`` filter on the
   *   ``/files`` listing, returning files across the entire cell.
   * - ``case`` carries the matching ``caseTag`` from ``useCasesList``.
   * - ``unresolved`` is a real case we cannot tag yet; the listing stays off,
   *   because an untagged request returns every case in the tenant.
   */
  const caseScope = useMemo(
    () => resolveCaseScope(casesQuery.data, caseId),
    [caseId, casesQuery.data],
  );
  const caseTag = caseScope.tag;
  const caseScopeUnresolved = caseScope.kind === 'unresolved';

  /**
   * Filters for the direct-CORS ``GET /provenance-service/v1/files`` listing.
   *
   * - ``searchQuery``: debounced user input, only sent once it crosses the
   *   3-character threshold (matches the existing UX). Surrounding quotes are
   *   stripped so an exact-phrase intent maps to a substring match.
   * - ``contentCategories``: chip-row file-type tokens only (PDF, MS Office,
   *   …). The ``ScopeDropdown`` (``selectedContentCategories``) is intentionally
   *   excluded — it scopes chat submission, not the file listing.
   * - ``tags`` / ``tagMatch``: present only when a specific case is selected;
   *   omitted entirely for All Cases so every file in the cell is returned.
   * - ``includeDeleted``: never sent.
   */
  const evidenceFilesFilters: ProvenanceFilesFilters = useMemo(() => {
    const trimmed = filesSearchTerm.trim();
    const unquoted =
      trimmed.startsWith('"') && trimmed.endsWith('"') ? trimmed.slice(1, -1) : trimmed;
    const chipKeys = allFileTypes
      ? []
      : Object.keys(activeFileTypes).filter((k) => activeFileTypes[k] === true);
    const contentCategories =
      chipKeys.length > 0 ? fileTypesTokensForEvidenceListing(chipKeys) : undefined;
    return {
      ...(unquoted.length > 2 ? { searchQuery: unquoted } : {}),
      ...(contentCategories ? { contentCategories } : {}),
      ...(caseTag ? { tags: [caseTag], tagMatch: 'any' as const } : {}),
    };
  }, [filesSearchTerm, allFileTypes, activeFileTypes, caseTag]);

  /** Scope value (case tag) → display label (case name) from provenance cases list. */
  const scopeLabelByTag = useMemo(() => {
    if (!casesQuery.data?.length) return {} as Record<string, string>;
    const m: Record<string, string> = {};
    for (const c of casesQuery.data) {
      const tag = c.caseTag?.trim();
      if (!tag) continue;
      m[tag] = getCaseDisplayName(c.caseName?.trim() ? c.caseName.trim() : tag);
    }
    return m;
  }, [casesQuery.data]);

  // For folder scopes *inside* a case, whose display path leads with the case's
  // `folder_token` segment. Case-tag scopes are already named by the map above.
  const caseFolderLabelMap = useMemo(
    () => caseFolderLabels(casesQuery.data),
    [casesQuery.data],
  );

  /** User-visible label for a scope tag: always prefer case name when known (never raw tag when cases list has a match). */
  const resolveScopeDisplay = useCallback(
    (scopeTag: string) => {
      const t = scopeTag.trim();
      const fromMap = scopeLabelByTag[t] ?? scopeLabelByTag[scopeTag];
      if (fromMap) return fromMap;
      const list = casesQuery.data;
      if (list?.length) {
        for (const c of list) {
          const ct = c.caseTag?.trim();
          if (!ct) continue;
          if (ct === t || ct === scopeTag) {
            return getCaseDisplayName(c.caseName?.trim() ? c.caseName.trim() : ct);
          }
        }
        const lastSeg = scopeTag.split('/').pop()?.trim();
        if (lastSeg && lastSeg !== scopeTag) {
          for (const c of list) {
            const ct = c.caseTag?.trim();
            if (ct && ct === lastSeg) {
              return getCaseDisplayName(c.caseName?.trim() ? c.caseName.trim() : ct);
            }
          }
        }
      }
      return relabelCaseFolderSegments(
        removeMD5Hash(
          stripUnwantedLeadingPathSegments(
            folderPathForEvidenceLockerDisplay(scopeTag, caseId),
          ),
        ),
        caseFolderLabelMap,
      );
    },
    [scopeLabelByTag, caseId, casesQuery.data, caseFolderLabelMap],
  );

  /** S3 folder segment only — used in chat request body ``scopes`` / ``excludedScopes`` (never case tags). */
  const caseFolderNameForChatPayload = useCallback(
    (scopeTag: string): string | undefined =>
      caseFolderNameForChatScopeTag(scopeTag, casesQuery.data),
    [casesQuery.data],
  );

  const mapScopeTagsToFolderNamesForChat = useCallback(
    (tags: string[]) => mapScopeTagsToFolderNames(tags, caseFolderNameForChatPayload),
    [caseFolderNameForChatPayload],
  );

  const handlePromptLibraryOpenChange = useCallback((open: boolean) => {
    if (open) {
      setSuppressPromptLibraryTooltip(false);
    } else {
      // Modal focus restoration can land back on the trigger after onOpenChange,
      // which makes TpTooltip reopen without a fresh hover (TR-2860).
      setSuppressPromptLibraryTooltip(true);
      requestAnimationFrame(() => {
        document.getElementById('prompt-library-open-button')?.blur();
      });
    }
    setIsPromptLibraryOpen(open);
  }, []);

  // Handle external trigger of prompt library
  useEffect(() => {
    if (triggerPromptLibrary) {
      handlePromptLibraryOpenChange(true);
    }
  }, [triggerPromptLibrary, handlePromptLibraryOpenChange]);

  const handleSetPreviewPopoverOpen = (index: number) => {
    openEvidenceLocker();
    setPreviewPopoverOpen(index);
    if (index === -1) {
      setPreviewPopoverUrl(null);
    }
    setShowObfuscatedPreview(true);
    const sourceItem = availableEvidence[index];
    if (!!sourceItem) {
      setPreviewPopoverUrl(null);
    }
    if (!linkedMediaItem) {
      // Clear both together: the app-level fullscreen viewer only mounts when
      // ``isFullscreen && fullscreenMediaItem`` are set, so nulling the item
      // without exiting fullscreen would hide the UI with no overlay/exit control.
      setFullscreenMediaItem(null);
      setIsFullscreen(false);
    }
  }

  /**
   * Opens the source preview popover for a row that isn't tracked in
   * ``availableEvidence`` (subdocuments, virtual tree nodes). Uses a separate
   * url-keyed state so the existing index-keyed preview logic for top-level
   * files is left untouched.
   */
  const handleSetPreviewPopoverOpenForUrl = (url: string | null) => {
    setPreviewPopoverUrl(url);
    if (url) {
      openEvidenceLocker();
      setPreviewPopoverOpen(-1);
      if (!linkedMediaItem) {
        // Clear both together so we never leave ``isFullscreen`` on without a
        // mounted fullscreen viewer (which would hide the UI with no exit control).
        setFullscreenMediaItem(null);
        setIsFullscreen(false);
      }
    }
  }

  useEffect(() => {
    const wasFullscreen = prevIsFullscreenRef.current;
    prevIsFullscreenRef.current = isFullscreen;

    if (!wasFullscreen && isFullscreen) {
      popoverStateRef.current = {
        index: previewPopoverOpenRef.current,
        url: previewPopoverUrlRef.current,
      };
      return;
    }

    if (wasFullscreen && !isFullscreen) {
      const saved = popoverStateRef.current;
      popoverStateRef.current = null;
      if (!saved) return;
      // Restore whichever preview mode was open so Escape returns to the locker preview.
      if (saved.url) {
        setPreviewPopoverUrl(saved.url);
        setPreviewPopoverOpen(-1);
        openEvidenceLocker();
      } else if (saved.index !== -1) {
        setPreviewPopoverOpen(saved.index);
        openEvidenceLocker();
      }
    }
  }, [isFullscreen, openEvidenceLocker]);

  useEffect(() => {
    if (!isResetting) {
      setIsAlertOpen(false);
    }
  }, [isResetting]);

  useEffect(() => {
    if (!isExpanded || !lockerUiOpen) {
      setPreviewPopoverOpen(-1);
      setPreviewPopoverUrl(null);
    }
  }, [lockerUiOpen, isExpanded]);

  useEffect(() => {
    if (holdSubmitVisualLock) {
      setLocalIsLoading(true);
      return;
    }
    setLocalIsLoading(isLoading);
  }, [holdSubmitVisualLock, isLoading]);

  useEffect(() => {
    if (evidenceFilter && evidenceFilter.length > 0 && evidenceFilter.trim() !== '') {
      setEvidenceInputValue(evidenceFilter);
      setScopeInputValue(evidenceFilter);
      openEvidenceLocker();
    }
  }, [evidenceFilter, openEvidenceLocker]);

  useEffect(() => {
    if (isExpanded) {
      // Set a timeout to match the animation duration (0.7s from the motion.div)
      const timer = setTimeout(() => {
        setIsFullyExpanded(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setIsFullyExpanded(false);
    }
  }, [isExpanded]);

  useEffect(() => {
    setIsBarTransitioning(true);
    handlePromptLibraryOpenChange(false);
    setFilterPickerOpen(false);
    const timer = window.setTimeout(() => {
      setIsBarTransitioning(false);
    }, BAR_LAYOUT_TRANSITION_MS);
    return () => window.clearTimeout(timer);
  }, [isExpanded, handlePromptLibraryOpenChange]);

  const barInteractionLocked = isBarTransitioning || holdSubmitVisualLock;

  useEffect(() => {
    if (!isExpanded || !shouldFocusTextareaAfterExpandRef.current) {
      return;
    }

    shouldFocusTextareaAfterExpandRef.current = false;
    const frameId = window.requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea || holdSubmitVisualLock) {
        return;
      }
      textarea.focus({ preventScroll: true });
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [holdSubmitVisualLock, isExpanded, textareaRef]);

  const expandQuestionBar = useCallback(() => {
    if (barInteractionLocked || isExpanded) {
      return;
    }
    shouldFocusTextareaAfterExpandRef.current = true;
    setIsExpanded(true);
  }, [barInteractionLocked, isExpanded, setIsExpanded]);

  const handleIncludeAllChange = useCallback((_filterKey: string, _checked: boolean) => {
    // Kept for backward compatibility; scope selection now handled via ScopeDropdown
  }, []);

  const submitUiLocked = holdSubmitVisualLock || isLoading || localIsLoading;

  const handleSubmit = async (
    e: React.FormEvent,
    useDeepDive: boolean = false,
    useMobile: boolean = false,
  ) => {
    if (featureFlags?.filterSizeLimit && isFilterTooBig(featureFlags?.filterSizeLimit as unknown as number, scopesAndFiles, caseId)) {
      return;
    }
    e.preventDefault();
    if (!textareaRef?.current?.value || textareaRef?.current?.value?.trim() === '' || submitUiLocked) return;
    setLocalIsLoading(true);
    setQuery(textareaRef?.current?.value || '');

    if (useDeepDive) {
      setStrandsModeQuerySent(true);
      setTimeout(() => {
        setStrandsModeQuerySent(false);
      }, 1000);
    }
    if (useMobile) {
      setMobileModeQuerySent(true);
      setTimeout(() => {
        setMobileModeQuerySent(false);
      }, 1000);
    }

    try {
      await onSubmit(
        textareaRef.current?.value || '',
        buildQuestionSubmitScopes({
          scopesAndFiles,
          contentCategoriesEnabled,
          mapScopeTags: mapScopeTagsToFolderNamesForChat,
        }),
        userId,
        sessionId,
        useDeepDive,
        useMobile,
      );
      collapseSources();
    } catch (error) {
      console.error('Error submitting query:', error);
      if (!holdSubmitVisualLock) {
        setLocalIsLoading(false);
      }
      // Handle error (e.g., show an error message to the user)
    }
  };

  const isNativeClipboardShortcut = (e: React.KeyboardEvent | KeyboardEvent) =>
    (e.metaKey || e.ctrlKey) && /^[cCvVxX]$/.test(e.key);

  const handleTextareaCopy = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // Isolate from handlers that assume document.getSelection() (empty in textareas).
    e.stopPropagation();
  };

  const handleTextareaPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isNativeClipboardShortcut(e)) {
      // answer-item registers a document-level Cmd+C handler that overwrites
      // the clipboard; stop propagation so the textarea keeps native copy/paste.
      e.stopPropagation();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      if (featureFlags?.filterSizeLimit && isFilterTooBig(featureFlags?.filterSizeLimit as unknown as number, scopesAndFiles, caseId)) {
        setFilterIsTooBig(true);
        return;
      }
      setFilterIsTooBig(false);
      e.preventDefault();

      // Create a synthetic button click event
      const button = assistantQueryButtonRef.current;
      if (button) {
        const syntheticEvent = {
          target: button,
          currentTarget: button,
          preventDefault: () => { },
          type: 'click'
        } as unknown as React.MouseEvent<HTMLButtonElement>;
        const statsContextLocal = {
          userInfo: {
            userId: statsContext?.userInfo?.userId || userId || '',
            sessionId: statsContext?.userInfo?.sessionId || sessionId || '',
            caseId: statsContext?.userInfo?.caseId || caseId || '',
            query: textareaRef.current?.value || ''
          }
        };
        statsContext.recordStat(
          'AssistantQueryButtonClicked',
          syntheticEvent,
          statsContextLocal        );
      }
      handleSubmit(e as unknown as React.FormEvent, false);
    }
  };

  const handleAddScopesAndFiles = ({
    scopesToAdd, filesToAdd, excludedScopesToAdd, excludedFilesToAdd
  }: {
    scopesToAdd: string[], filesToAdd: string[], excludedScopesToAdd: string[], excludedFilesToAdd: string[]
  }) => {
    const newScopes = new Set([...(scopesAndFiles?.scopes || []), ...scopesToAdd]);
    const newFiles = new Set([...(scopesAndFiles?.files || []), ...filesToAdd]);
    const newExcludedScopes = new Set([...(scopesAndFiles?.excludedScopes || []), ...excludedScopesToAdd]);
    const newExcludedFiles = new Set([...(scopesAndFiles?.excludedFiles || []), ...excludedFilesToAdd]);
    const newScopesAndFiles = {
      scopes: Array.from(newScopes),
      files: Array.from(newFiles),
      excludedScopes: Array.from(newExcludedScopes),
      excludedFiles: Array.from(newExcludedFiles),
    };
    setScopesAndFiles((prev) => ({
      ...newScopesAndFiles,
      contentCategories: prev.contentCategories,
      excludedContentCategories: prev.excludedContentCategories,
      evidenceCategoryChips: prev.evidenceCategoryChips,
      excludedEvidenceCategoryScopes: prev.excludedEvidenceCategoryScopes,
      evidenceCategoryScopes: prev.evidenceCategoryScopes,
      evidenceAllFileTypes: prev.evidenceAllFileTypes,
    }));
    setSelectedScopes([]);
    setSelectedEvidence([]);
    setExcludedScopes([]);
    setExcludedEvidence([]);
  }

  const changeHeight = (elem: HTMLTextAreaElement) => {
    if (elem.value.length < 70) {
      elem.style.height = '20px';
    } else {
      elem.style.height = elem.scrollHeight + 'px';
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    changeHeight(e.target);
    setHasText(!!e.target.value.trim());
    setLocalQuery(e.target.value);
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.value = localQuery;
      changeHeight(textareaRef.current);
    }
  }, [localQuery]);

  useEffect(() => {
    setLocalQuery(query);
    if (textareaRef.current) {
      textareaRef.current.value = query;
      changeHeight(textareaRef.current);
      setHasText(!!query.trim());
    }
  }, [query]);

  useEffect(() => {
    if (caseId === caseIdRef.current) {
      return;
    }
    const previousCaseId = caseIdRef.current;
    caseIdRef.current = caseId || null;
    if (previousCaseId == null || previousCaseId.trim() === '') {
      return;
    }
    // Reset the question bar's local UI state on case change. Do NOT reset
    // `scopesAndFiles` here: it is owned by the parent and the parent's
    // case-switch effect rehydrates it from the new case's per-case
    // localStorage snapshot using a raw (non-persisting) setter. Calling
    // `setScopesAndFiles({...empty})` through the persisting
    // `handleSetScopesAndFiles` prop would clobber the new case's
    // localStorage snapshot before the parent can read it.
    setLocalQuery('');
    setHasText(false);
    setQuery('');
    if (textareaRef.current) {
      textareaRef.current.value = '';
      changeHeight(textareaRef.current);
    }
    setScopeInputValue('');
    setEvidenceInputValue('');
    setActiveFileTypes({});
    setAllFileTypes(false);
  }, [caseId, setQuery, textareaRef]);

  /** Folder scope options: only for "all cases" (`caseId === '*'`); provenance list via `useCasesList`. */
  useEffect(() => {
    if (!accessToken || !caseId || caseId.trim() === '') {
      setAllScopes([]);
      setAvailableScopes([]);
      setLoadingScopes(false);
      return;
    }
    if (caseId !== '*') {
      setAllScopes([]);
      setAvailableScopes([]);
      setLoadingScopes(false);
      return;
    }
    const listLoading =
      casesQuery.isLoading ||
      (casesQuery.isPending && casesQuery.data === undefined && !casesQuery.isError);
    setLoadingScopes(listLoading);
    const list = casesQuery.data;
    if (!list?.length) {
      setAllScopes([]);
      setAvailableScopes([]);
      return;
    }
    const rows = list
      .filter((c) => c.caseTag.trim() !== '' && c.caseTag !== caseId)
      .map((c) => ({
        tag: c.caseTag,
        label: getCaseDisplayName(c.caseName?.trim() ? c.caseName.trim() : c.caseTag),
      }));
    rows.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
    const sortedTags = rows.map((r) => r.tag);
    setAllScopes(sortedTags);
    setAvailableScopes(sortedTags.slice(0, 250));
  }, [
    caseId,
    accessToken,
    casesQuery.data,
    casesQuery.isLoading,
    casesQuery.isPending,
    casesQuery.isError,
  ]);

  useEffect(() => {
    if (featureFlags?.filterSizeLimit && isFilterTooBig(featureFlags?.filterSizeLimit as unknown as number, scopesAndFiles, caseId)) {
      setFilterIsTooBig(true);
    } else {
      setFilterIsTooBig(false);
    }
  }, [scopesAndFiles, caseId, featureFlags?.filterSizeLimit]);

  useEffect(() => {
    if (scopeSearchTerm.trim().length > 2) {
      let scopeSearchTermToUse = scopeSearchTerm;
      if (scopeSearchTermToUse.startsWith('"') && scopeSearchTermToUse.endsWith('"')) {
        scopeSearchTermToUse = scopeSearchTermToUse.slice(1, -1);
      } else {
        scopeSearchTermToUse = scopeSearchTermToUse.trim();
      }
      const q = scopeSearchTermToUse.toLowerCase();
      setAvailableScopes(allScopes.filter((tag: string) => {
        const label = scopeLabelByTag[tag] ?? tag;
        return tag.toLowerCase().includes(q) || label.toLowerCase().includes(q);
      }));
    } else {
      setAvailableScopes(allScopes);
    }
  }, [scopeSearchTerm, allScopes, scopeLabelByTag]);

  /**
   * Direct-CORS files listing for the EvidenceLocker.
   *
   * Replaces the legacy ``POST /api/protected/provenance-service-proxy`` BFF
   * fetch with the offset/limit-paginated ``GET /provenance-service/v1/files``
   * endpoint via ``useProvenanceFiles`` (TanStack ``useInfiniteQuery``). The
   * hook handles cell-aware URL building, Cognito access-token auth, and
   * cancellation; ``shouldLoadEvidenceFiles`` keeps it disabled in folder mode
   * until a chip / "All Files" / scope category is active.
   */
  const filesQuery = useProvenanceFiles(evidenceFilesFilters, {
    enabled: shouldLoadEvidenceFiles && !caseScopeUnresolved,
    pageSize: 100,
  });

  /** Reset selections when the case or debounced search term changes. */
  useEffect(() => {
    setSelectedEvidence([]);
    setSelectedScopes([]);
  }, [caseId, filesSearchTerm]);

  /** Mirror hook loading state into the existing ``loadingEvidence`` flag used by the UI. */
  useEffect(() => {
    if (!shouldLoadEvidenceFiles) {
      setLoadingEvidence(false);
      return;
    }
    setLoadingEvidence(filesQuery.isLoading || filesQuery.isFetching);
  }, [shouldLoadEvidenceFiles, filesQuery.isLoading, filesQuery.isFetching]);

  /**
   * Sync the flattened ``FileSummary`` pages into the existing
   * ``availableEvidence`` state so the rest of the EvidenceLocker UI
   * (selection, preview, chat-submit payload) keeps working unchanged.
   *
   * - ``url`` ← canonicalized ``searchablePath``. Upstream ``/files`` strips
   *   the ``data/bulk/`` prefix from ``searchablePath`` for display, but
   *   downstream provenance lookups (``/files/file-info``, ``/files/file-status``)
   *   key off the canonical key WITH that prefix. EvidenceLocker only ever lists
   *   ingested source files (which always live under ``data/bulk/``), so it's
   *   safe to re-prepend here. Other surfaces that consume different file kinds
   *   must NOT do this — keep the prefixing local to this mapping.
   * - ``mediaType`` ← ``extension`` (lowercased file type).
   * - ``status`` ← ``status`` (defaults to ``'unknown'``).
   */
  useEffect(() => {
    // A disabled query still reads the untagged All Cases cache entry, so an
    // unresolved case must not consume it.
    if (!shouldLoadEvidenceFiles || caseScopeUnresolved) {
      setAvailableEvidence([]);
      return;
    }
    if (!filesQuery.data) return;
    const items = filesQuery.data.pages.flatMap((p) => p.items) as FileSummary[];
    const mapped: EvidenceObject[] = items.map((f) => ({
      url: toProvenanceLookupPath(f.searchablePath),
      mediaType: f.extension ?? '',
      status: (f.status ?? 'unknown') as string,
      pageCount: f.pageCount ?? null,
      previewUrl: f.previewUrl ?? null,
      contentModerationType:
        canSeeCsamTags &&
        f.contentModeration?.type &&
        f.contentModeration.findingStatus !== 'REVIEWED'
          ? f.contentModeration.type
          : null,
    }));
    setAvailableEvidence(mapped);
  }, [
    filesQuery.data,
    shouldLoadEvidenceFiles,
    caseScopeUnresolved,
    canSeeCsamTags,
  ]);

  useEffect(() => {
    let newEvidenceSources: EvidenceObject[] = [];
    availableEvidence?.forEach((files: EvidenceObject) => {
      newEvidenceSources.push(files);
    });
    setAllEvidence(newEvidenceSources);
  }, [availableEvidence]);

  const hotkeyHandlers = {
    'a': (e: KeyboardEvent) => {
      const activeElement = document.activeElement;

      // Only proceed if the active element is the body (no focused elements)
      if (activeElement === document.body) {
        e.preventDefault();
        expandQuestionBar();
      }
    }
  };

  useHotkeys(
    Object.keys(hotkeyHandlers).join(', '),
    (e, handler) => {
      if (Array.isArray(handler.keys)) {
        const key = handler.keys[0];
        if (key in hotkeyHandlers) {
          hotkeyHandlers[key as keyof typeof hotkeyHandlers]?.(e);
        }
      }
    },
    {
      enableOnFormTags: true,
      preventDefault: false,
      enabled: true
    }
  );

  const handlePromptSelect = (selectedQuery: string) => {
    // Focus the textarea
    textareaRef.current?.focus();

    // Update the textarea value
    if (textareaRef.current) {
      textareaRef.current.value = selectedQuery;
    }

    // Update the query state
    setLocalQuery(selectedQuery);

    // Update the hasText state
    setHasText(!!selectedQuery.trim());
  };

  useEffect(() => {
    const isQuestionBarInteractiveTarget = (target: Node | null): boolean => {
      if (!target || !(target instanceof Element)) {
        return false;
      }
      if (formRef.current?.contains(target)) {
        return true;
      }
      if (previewPopoverRef.current?.contains(target)) {
        return true;
      }
      if (promptLibraryRef.current?.contains(target)) {
        return true;
      }
      if (target.closest('#prompt-library-modal, #start-new-session-confirm-modal')) {
        return true;
      }
      return !!target.closest(
        '[data-question-bar-interactive], #evidence-filter-picker-root, [data-evidence-filter-picker-menu], #fullScreenMediaDisplayOuter',
      );
    };

    const handleDocumentClick = (event: MouseEvent) => {
      if (holdSubmitVisualLock || barInteractionLocked || !isExpanded) {
        return;
      }
      if (isQuestionBarInteractiveTarget(event.target as Node)) {
        return;
      }
      setIsExpanded(false);
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [isSmallScreen, formRef, setIsExpanded, isExpanded, holdSubmitVisualLock, barInteractionLocked]);

  useEffect(() => {
    if (!isExpanded) {
      if (textareaRef.current) {
        textareaRef.current.style.height = '0px';
      }
      if (isFilterVisible) {
        setIsFilterVisible(false);
      }
      if (isEvidenceLockerModalOpen) {
        setIsEvidenceLockerModalOpen(false);
      }
      // TODO: the code below was causing mediaDisplay to be empty when feedback was opened
      // if (fullscreenMediaItem !== null && !linkedMediaItem) {
      //   setFullscreenMediaItem(null);
      // }
      if (previewPopoverOpen !== -1) {
        setPreviewPopoverOpen(-1);
      }
    } else if (textareaRef.current) {
      changeHeight(textareaRef.current);
      setHasText(!!textareaRef.current.value.trim());
    }
  }, [isExpanded]);

  const addToSelectedSources = (sourceUrl: string) => {
    if (!selectedEvidence.includes(sourceUrl)) {
      setSelectedEvidence([...selectedEvidence, sourceUrl]);
      setExcludedEvidence(excludedEvidence.filter((source) => source !== sourceUrl));
      setIsExcludeAllActive(false);
      setIsIncludeAllActive(false);
    } else {
      setSelectedEvidence(selectedEvidence.filter((source) => source !== sourceUrl));
      setIsExcludeAllActive(false);
      setIsIncludeAllActive(false);
    }
  };

  const addToSelectedScopes = (scopeIn: string) => {
    if (!selectedScopes.includes(scopeIn)) {
      setSelectedScopes([...selectedScopes, scopeIn]);
      setExcludedScopes([]);
      setIsExcludeAllActive(false);
      setIsIncludeAllActive(false);
    } else {
      setSelectedScopes(selectedScopes.filter((scope) => scope !== scopeIn));
      setIsExcludeAllActive(false);
      setIsIncludeAllActive(false);
    }
  };

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [isExcludeAllActive, setIsExcludeAllActive] = useState(false);
  const [isIncludeAllActive, setIsIncludeAllActive] = useState(false);

  const getIconStyle = (isActive: boolean) => {
    return `cursor-pointer hover:text-black ${isActive ? 'text-black' : 'text-brand-blue-light'}`;
  };

  const getItemStyle = (isExcluded: boolean) => {
    return isExcluded
      ? 'border-red-500 border-solid bg-red-100'
      : 'border-[#2d8dfe] border-solid bg-[#99C4F6]/30';
  };

  const removeScopeOrFile = (fileOrScope: string) => {
    let newScopesAndFiles = { ...scopesAndFiles };
    if (scopesAndFiles?.files.includes(fileOrScope)) {
      newScopesAndFiles.files = scopesAndFiles.files.filter(f => f !== fileOrScope);
      setScopesAndFiles(newScopesAndFiles);
    } else if (scopesAndFiles?.scopes.includes(fileOrScope)) {
      newScopesAndFiles.scopes = scopesAndFiles.scopes.filter(s => s !== fileOrScope);
      setScopesAndFiles(newScopesAndFiles);
    } else if (scopesAndFiles?.excludedFiles.includes(fileOrScope)) {
      newScopesAndFiles.excludedFiles = scopesAndFiles.excludedFiles.filter(f => f !== fileOrScope);
      setScopesAndFiles(newScopesAndFiles);
    } else if (scopesAndFiles?.excludedScopes.includes(fileOrScope)) {
      newScopesAndFiles.excludedScopes = scopesAndFiles.excludedScopes.filter(s => s !== fileOrScope);
      setScopesAndFiles(newScopesAndFiles);
    }
  };

  const getHoverButtonStyle = (fileOrScope: string) => {
    const isExcluded = scopesAndFiles?.excludedFiles?.includes(fileOrScope) || scopesAndFiles?.excludedScopes?.includes(fileOrScope);
    const handleClick = (e: React.SyntheticEvent) => {
      e.stopPropagation();
      let newScopesAndFiles = scopesAndFiles ? { ...scopesAndFiles } : { scopes: [], files: [], excludedScopes: [], excludedFiles: [] };
      if (scopesAndFiles?.files.includes(fileOrScope)) { // remove from files and add to excludedFiles
        newScopesAndFiles.excludedFiles = [...(scopesAndFiles.excludedFiles || []), fileOrScope];
        newScopesAndFiles.files = scopesAndFiles.files.filter(f => f !== fileOrScope);
      } else if (scopesAndFiles?.scopes.includes(fileOrScope)) { // remove from scopes and add to excludedScopes
        newScopesAndFiles.excludedScopes = [...(scopesAndFiles.excludedScopes || []), fileOrScope];
        newScopesAndFiles.scopes = scopesAndFiles.scopes.filter(s => s !== fileOrScope);
      } else if (scopesAndFiles?.excludedFiles.includes(fileOrScope)) { // remove from excludedFiles and add to files
        newScopesAndFiles.files = [...(scopesAndFiles.files || []), fileOrScope];
        newScopesAndFiles.excludedFiles = scopesAndFiles.excludedFiles.filter(f => f !== fileOrScope);
      } else if (scopesAndFiles?.excludedScopes.includes(fileOrScope)) { // remove from excludedScopes and add to scopes
        newScopesAndFiles.scopes = [...(scopesAndFiles.scopes || []), fileOrScope];
        newScopesAndFiles.excludedScopes = scopesAndFiles.excludedScopes.filter(s => s !== fileOrScope);
      }
      setScopesAndFiles(newScopesAndFiles);
    };

    return isExcluded
      ? (
        <div className="absolute right-[1px] top-1/2 -translate-y-1/2">
          <div className="flex flex-row items-center justify-center gap-1 bg-black rounded-full p-1">
            <Button
              type="button"
              iconOnly
              size="sm"
              style="text"
              color="light"
              background="inverse"
              tabIndex={0}
              aria-label={t('chat.evidence.includeItem')}
              className="cursor-pointer text-white/40 hover:text-white/80 focus:outline focus:outline-2 focus:outline-white rounded"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClick(e);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClick(e);
                }
              }}
            >
              <PlusCircleIcon weight="regular" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="absolute right-[1px] top-1/2 -translate-y-1/2">
          <div className="flex flex-row items-center justify-center gap-1 bg-black rounded-full p-1">
            <Button
              type="button"
              iconOnly
              size="sm"
              style="text"
              color="light"
              background="inverse"
              tabIndex={0}
              aria-label={t('chat.evidence.excludeItem')}
              className="cursor-pointer text-white/40 hover:text-white/80 focus:outline focus:outline-2 focus:outline-white rounded"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClick(e);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClick(e);
                }
              }}
            >
              <MinusCircleIcon weight="regular" />
            </Button>
          </div>
        </div>
      )
  };

  const toggleFileType = (fileType: string) => {
    const updatedActiveFileTypes = getNewActiveFileTypes(fileType, activeFileTypes);

    if (Object.values(updatedActiveFileTypes).some((value) => value === true)) {
      setAllFileTypes(false);
    }
    setActiveFileTypes(updatedActiveFileTypes);
  };

  const removeExcludedContentCategory = (fileType: string) => {
    setScopesAndFiles((prev) => {
      const excludedScopeCats = (prev.excludedEvidenceCategoryScopes ?? []).filter(
        (ft) => ft !== fileType,
      );
      return {
        ...prev,
        ...evidenceCategoryScopeFieldsWithWireTokens(
          prev.evidenceCategoryScopes ?? [],
          excludedScopeCats,
          !!prev.evidenceAllFileTypes,
        ),
      };
    });
  };

  const removeContentCategory = (fileType: string) => {
    setScopesAndFiles((prev) => {
      const includedScopeCats = (prev.evidenceCategoryScopes ?? []).filter(
        (ft) => ft !== fileType,
      );
      return {
        ...prev,
        ...evidenceCategoryScopeFieldsWithWireTokens(
          includedScopeCats,
          prev.excludedEvidenceCategoryScopes ?? [],
          !!prev.evidenceAllFileTypes,
        ),
      };
    });
  };

  const toggleShowFolders = () => {
    setShowFolders(!showFolders);
  };

  const toggleAllFileTypes = () => {
    const newAllFileTypes = !allFileTypes;
    if (!allFileTypes) {
      setActiveFileTypes({});
    }
    setAllFileTypes(newAllFileTypes);
  };

  const activeFileTypeChipCount = Object.keys(activeFileTypes).filter(
    (key) => activeFileTypes[key] === true,
  ).length;
  const evidenceFilterActive = hasEvidenceFilterActive(scopesAndFiles);
  const evidenceLockerCategoryScopeActive =
    contentCategoriesEnabled && !allFileTypes && activeFileTypeChipCount > 0;

  const clearAllEvidenceFilters = useCallback(() => {
    setScopesAndFiles({
      scopes: [],
      files: [],
      excludedScopes: [],
      excludedFiles: [],
      contentCategories: [],
      excludedContentCategories: [],
      evidenceCategoryChips: [],
      excludedEvidenceCategoryScopes: [],
      evidenceCategoryScopes: [],
      evidenceAllFileTypes: false,
    });
    setShowFolders(false);
    onAssistantEvidenceFilterCleared?.();
    setPreviewPopoverOpen(-1);
    setFullscreenMediaItem(null);
    setFilterPickerOpen(false);
    setIsEvidenceLockerModalOpen(false);
  }, [
    onAssistantEvidenceFilterCleared,
    setScopesAndFiles,
    setShowFolders,
  ]);

  const handleNewModalFunnelClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (lockerUiOpen) {
        setIsEvidenceLockerModalOpen(false);
        setFilterPickerOpen(true);
        return;
      }

      setFilterPickerOpen((open) => !open);
    },
    [lockerUiOpen],
  );
  /** Folder mode hides file rows unless a type filter applies; scope dropdown counts as one. */
  const showEvidenceRowsWhenFoldersOn =
    !showFolders || allFileTypes || activeFileTypeChipCount > 0 || evidenceLockerCategoryScopeActive;

  const handleSelectAll = () => {
    setExcludedEvidence([]);
    setExcludedScopes([]);
    // Ask the tree what's on-screen right now (handles folder navigation,
    // search, and subdocuments). Falls back to the legacy flat list if the
    // tree isn't mounted yet for any reason.
    const visible = evidenceTreeRef.current?.getVisibleSelection();
    if (visible) {
      if (showEvidenceRowsWhenFoldersOn) {
        setSelectedEvidence(Array.from(new Set(visible.fileUrls)));
      }
      setSelectedScopes(Array.from(new Set(visible.folderUrls)));
    } else {
      if (showEvidenceRowsWhenFoldersOn) {
        setSelectedEvidence(availableEvidence?.map((source) => (source as any).url) || []);
      }
      if (showFolders || (!allFileTypes && activeFileTypeChipCount < 1)) {
        setSelectedScopes(availableScopes || []);
      }
    }
    setIsIncludeAllActive(true);
    setIsExcludeAllActive(false);
  };

  const handleCopySourceForTimeline = async (source: Source, index: number) => {
    try {
      const pathToCopy = extractPathAfterBulk(source.sourceFilePath || source.truncatedFilePath || '').truncatedFilePath;
      source.sourceContext = buildTimelineCopySourceContext(source);
      const sourceToCopy = {
        source,
        caseId: caseId,
      }
      if (pathToCopy) {
        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(pathToCopy);
          } else {
            // Fallback for non-secure contexts
            const textArea = document.createElement('textarea');
            textArea.value = pathToCopy;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand('copy');
            textArea.remove();
          }
          sessionStorage.setItem('source', JSON.stringify(sourceToCopy));
          setCopiedSource(index);
        } catch (clipboardError) {
          console.warn('Clipboard operation failed:', clipboardError);
          // Still set the copied source even if clipboard fails
          sessionStorage.setItem('source', JSON.stringify(sourceToCopy));
          setCopiedSource(index);
        }
      }
    } catch (error) {
      console.error('Failed to copy source path:', error);
    }
  };

  const handleFocus = () => {
    // do nothing - but maybe stop polling
  };

  const handleBlur = () => {
    // do nothing - but maybe start polling
  };

  useEffect(() => {
    if (copiedSource !== -1) {
      setTimeout(() => {
        setCopiedSource(-1);
      }, 1500);
    }
  }, [copiedSource]);

  useEffect(() => {
    if (addedSource !== -1) {
      setTimeout(() => {
        setAddedSource(-1);
      }, 1500);
    }
  }, [addedSource]);

  useEffect(() => {
    if (isFullscreen) {
      return;
    }
    if (previewPopoverOpen !== -1 || previewPopoverUrl !== null) {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target;
        if (!(target instanceof Node)) return;
        if (
          target instanceof Element &&
          target.closest('[data-evidence-preview-dismiss-overlay]')
        ) {
          return;
        }
        if (
          previewPopoverRef.current &&
          !previewPopoverRef.current.contains(target)
        ) {
          event.stopPropagation();
          setPreviewPopoverOpen(-1);
          setPreviewPopoverUrl(null);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [previewPopoverOpen, previewPopoverUrl, isFullscreen]);

  // The locker modal, the filter menu and the preview backdrop are all portaled to
  // ``document.body``, so CSS-hiding Chat does not hide them (TR-3219).
  useDismissOnViewExit(assistantViewSelected, () => {
    setIsEvidenceLockerModalOpen(false);
    setFilterPickerOpen(false);
    setPreviewPopoverOpen(-1);
    setPreviewPopoverUrl(null);
  });

  /**
   * Server-reported total from ``GET /files`` ``pagination.total`` (per the
   * first page) so the "Showing the top N result(s)" label reflects the full
   * match count even when we've only loaded the first chunk via infinite
   * scroll. Falls back to the locally-known evidence length when the hook
   * hasn't returned data yet.
   */
  const evidenceTotal = caseScopeUnresolved
    ? 0
    : (filesQuery.data?.pages?.[0]?.total ?? availableEvidence?.length ?? 0);
  const totalResults =
    (showEvidenceRowsWhenFoldersOn ? evidenceTotal : 0) +
    (((!allFileTypes && activeFileTypeChipCount < 1) || showFolders)
      ? Math.min(availableScopes?.length || 0, 250)
      : 0);

  const isSourceCardPopoverOpen =
    (previewPopoverOpen !== -1 || previewPopoverUrl !== null) &&
    lockerUiOpen &&
    !useNewEvidenceLockerModal;

  const activePreviewUrl = useMemo(() => {
    if (previewPopoverUrl) return previewPopoverUrl;
    if (previewPopoverOpen >= 0) {
      const item = availableEvidence[previewPopoverOpen] as { url?: string } | undefined;
      if (item?.url) return item.url;
    }
    return null;
  }, [previewPopoverOpen, previewPopoverUrl, availableEvidence]);

  // The portaled blur mounts whenever preview state is set, but the in-tree
  // SourceCard popover only renders on file rows (not folders-only view) and
  // only when preview state resolves to a URL. Hide the fallback dismiss
  // control only in that case — otherwise the user gets an empty blur.
  const hasPreviewForeground =
    activePreviewUrl !== null && showEvidenceRowsWhenFoldersOn;

  /**
   * "Folders-only" view: the user clicked the Folders chip and has not
   * narrowed by file-type / content-category / All-files. In this mode we
   * swap the hierarchical tree for the flat ``/v1/folders`` endpoint via
   * the tree component's ``mode="folders"`` rendering — every folder
   * anywhere in the cell, no drill-in, no file rows.
   */
  const isFoldersOnlyMode = showFolders && !showEvidenceRowsWhenFoldersOn;

  const hasAnyPick =
    selectedEvidence.length > 0 ||
    selectedScopes.length > 0 ||
    excludedEvidence.length > 0 ||
    excludedScopes.length > 0;

  // `EvidenceLockerTree` takes its pick sets as props and isn't memoized,
  // so passing `new Set(selectedEvidence)` etc. inline forced a full re-render
  // of the virtualized tree (and every `TreeRow`) on every parent render.
  // Memoize so the Set reference only changes when the backing array does.
  const pickedEvidenceSet = useMemo(() => new Set(selectedEvidence), [selectedEvidence]);
  const excludedEvidenceSet = useMemo(() => new Set(excludedEvidence), [excludedEvidence]);
  const pickedScopesSet = useMemo(() => new Set(selectedScopes), [selectedScopes]);
  const excludedScopesSet = useMemo(() => new Set(excludedScopes), [excludedScopes]);

  // Render the Select All / Clear All controls inline with the tree's
  // breadcrumb (or search-results header). They previously floated above the
  // tree in a header row that became visually empty once the misleading
  // "Showing the top N result(s)" label was removed; placing them next to the
  // breadcrumb keeps them tightly bound to the view they affect.
  const lockerHeaderActions = (
    <>
      <Button
        type="button"
        style="text"
        color="accent"
        background="default"
        size="sm"
        aria-label={t('chat.evidence.clearAllSelections')}
        className={`flex-shrink-0 h-auto px-1 py-0 ${
          hasAnyPick
            ? 'text-brand-blue hover:text-black'
            : 'text-black/30 hidden'
        }`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (hasAnyPick) {
            setSelectedEvidence([]);
            setSelectedScopes([]);
            setExcludedEvidence([]);
            setExcludedScopes([]);
            setIsExcludeAllActive(false);
            setIsIncludeAllActive(false);
          }
        }}
      >
        {t('chat.evidence.clearAll')}
      </Button>
      <Button
        type="button"
        style="text"
        color="accent"
        background="default"
        size="sm"
        aria-label={t('chat.evidence.selectAllItems')}
        className={`text-brand-blue hover:text-black flex-shrink-0 h-auto px-1 py-0 ${
          isIncludeAllActive ? 'hidden' : ''
        }`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSelectAll();
        }}
      >
        {t('chat.evidence.selectAll')}
      </Button>
    </>
  );

  // Render the EvidenceLockerTree unconditionally when the evidence locker is
  // open. The tree owns its own loading / empty / error UX (see
  // ``evidence-locker-tree.tsx``) and — critically — owns its ``navStack``
  // breadcrumb state. Gating this render on a parent-side ``hasResults`` flag
  // (driven by the legacy flat ``availableEvidence`` list) caused the tree to
  // unmount whenever a chip filter returned zero files, dropping the user's
  // navigated depth on the next render.
  const renderResultsArea = (wrapperClassName: string) => (
    <div className={wrapperClassName}>
      <div className="w-full">
        {isFilterVisible && !useNewEvidenceLockerModal && (showEvidenceRowsWhenFoldersOn || isFoldersOnlyMode) && (
              <div className="h-[260px] border border-brand-blue-light/30 rounded-md overflow-hidden flex flex-col bg-white">
                <EvidenceLockerTree
                  ref={evidenceTreeRef}
                  mode={isFoldersOnlyMode ? 'folders' : 'tree'}
                  searchQuery={filesSearchTerm}
                  contentCategories={evidenceFilesFilters.contentCategories}
                  caseTag={caseTag}
                  caseScopeUnresolved={caseScopeUnresolved}
                  scopeCaseFolderMatchId={
                    caseId === '*' || caseId === 'All Cases' || !caseId?.trim() ? null : caseId.trim()
                  }
                  pickedPaths={pickedEvidenceSet}
                  excludedPaths={excludedEvidenceSet}
                  pickedFolderPaths={pickedScopesSet}
                  excludedFolderPaths={excludedScopesSet}
                  toUrl={toProvenanceLookupPath}
                  headerActions={lockerHeaderActions}
                  onTogglePick={(url) => addToSelectedSources(url)}
                  onTogglePickFolder={(url) => addToSelectedScopes(url)}
                  onClearSearch={() => { setEvidenceInputValue(''); setScopeInputValue(''); }}
                  renderRowPreview={({ url, mediaType, pageCount, node }) => {
                    // After the dev cutover, processing status lives on the
                    // typed ``fileInfo`` payload (``file.status`` for ingested
                    // source files, ``subdocument.status`` for processed
                    // outputs) — not the legacy untyped ``metadata`` blob.
                    const fi = node.fileInfo;
                    const provenanceStatus =
                      fi?.kind === 'file'
                        ? fi.file.status
                        : fi?.kind === 'subdocument'
                          ? fi.subdocument.status
                          : undefined;
                    const provenanceStatusMessage =
                      fi?.kind === 'file'
                        ? fi.file.statusMessage
                        : fi?.kind === 'subdocument'
                          ? fi.subdocument.statusMessage
                          : undefined;
                    const provenanceStatusTooltip =
                      isStatus(provenanceStatus, 'SKIPPED') && provenanceStatusMessage?.trim()
                        ? `Status: ${provenanceStatus} - ${provenanceStatusMessage.trim()}`
                        : formatProvenanceStatusLabel(provenanceStatus, 'UNKNOWN');
                    const index = availableEvidence.findIndex((e) => (e as { url: string }).url === url);
                    // Rows that aren't in the flat ``/files`` list (subdocs +
                    // virtual tree entries) get a url-keyed popover so the
                    // eyeball still opens a SourceCard for them.
                    const sourceObject = index >= 0 ? (availableEvidence[index] as EvidenceObject) : null;
                    const fileName = url.split('/').pop() ?? url;
                    const isOpenByIndex = index >= 0 && previewPopoverOpen === index;
                    const isOpenByUrl = index < 0 && previewPopoverUrl === url;
                    let useMetaViewer = false;
                    let useEmailViewer = false;
                    let s3Key = '';
                    if (mediaType === 'html' || /^(html|htm)$/i.test(mediaType)) {
                      s3Key = url.startsWith('s3://') ? url.split('/').slice(3).join('/') : url;
                      const lastPart = (s3Key.split('/').pop() || '').toLowerCase();
                      if (META_FILE_REGEX.test(lastPart)) {
                        useMetaViewer = true;
                      }
                    }
                    if (!useMetaViewer) {
                      const emailKey = emailViewerS3KeyFromUrl(url);
                      if (emailKey) {
                        useEmailViewer = true;
                        s3Key = emailKey;
                      }
                    }
                    // Container types (zip, ufdr, …) only render meaningfully
                    // via their extracted children; the preview control would
                    // open a SourceCard with nothing to show. Mailbox
                    // containers (mbox / pst / ost) open ``/email-viewer``.
                    const fileSummary = fileSummaryFromTreeNode(node);
                    const isContainer = isContainerLikeSource(
                      { sourceFilePath: url } as Source,
                      fileSummary,
                    );
                    const isUfdrRoot =
                      themeUfdrEnabled &&
                      (isUfdrRootSource(
                        { sourceFilePath: url } as Source,
                        fileSummary,
                      ) ||
                        isZipExportRoot(
                          { sourceFilePath: url } as Source,
                          fileSummary,
                        ));
                    const showPreviewButton = !isContainer || useEmailViewer || isUfdrRoot;
                    // Prefer CM data embedded on the tree node so deeply nested
                    // files — which may never appear in the first page of the
                    // flat ``/files`` list that ``availableEvidence`` is built
                    // from — are still flagged, gated and obfuscated. Falls back
                    // to the flat-list entry for rows whose node carries no
                    // file-level moderation data.
                    // Tags, the eye-slash warning, and the acknowledgement gate
                    // follow ``canSeeCsamTags``. Tenant access off hides all of
                    // them, including moderation data that only exists on the
                    // tree node.
                    const nodeCmType =
                      fileSummary?.contentModeration?.type &&
                      fileSummary.contentModeration.findingStatus !== 'REVIEWED'
                        ? fileSummary.contentModeration.type
                        : null;
                    // Tree nodes still carry contentModeration when tenant
                    // access is off. Badges, the warning, and the ack gate
                    // follow canSeeCsamTags, same as the flat evidence list.
                    const effectiveCmType = canSeeCsamTags
                      ? (sourceObject?.contentModerationType ?? nodeCmType)
                      : null;
                    const effectivePreviewUrl =
                      sourceObject?.previewUrl ?? fileSummary?.previewUrl ?? null;
                    const isCmAckActive =
                      (index >= 0 && contentModerationAckIndex === index) ||
                      (index < 0 && contentModerationAckUrl === url);
                    return (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <TpTooltip
                          placement="left"
                          delayDuration={100}
                          sideOffset={4}
                          content={provenanceStatusTooltip}
                        >
                          <div
                            className="flex flex-col items-center justify-center rounded-md transition-colors duration-200 bg-white/50 hover:bg-white/70 min-w-[28px] min-h-[28px]"
                            role="status"
                            aria-label={provenanceStatusTooltip}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <div className="bg-white rounded-full p-0.5 flex items-center justify-center">
                              <ProvenanceIcon
                                status={provenanceStatus}
                                size={getProvenanceIconDisplaySize(provenanceStatus)}
                                className={getProvenanceToolbarIconClassNames(provenanceStatus)}
                                aria-hidden
                              />
                            </div>
                          </div>
                        </TpTooltip>
                        {showPreviewButton && (
                        <>
                        <Popover
                          placement='right-end'
                          showArrow={false}
                          shouldFlip={true}
                          offset={isSmallScreen ? 4 : 8}
                          autoFocus={true}
                          className="outline-none"
                          classNames={{
                            base: 'outline-none before:bg-gray-600 before:rounded-[2px] before:shadow-none before:drop-shadow-none border-0 before:w-[25px] before:h-[25px] mr-[-50px] sm:mr-0 h-screen sm:h-auto w-screen sm:w-auto ',
                            content: 'p-0',
                          }}
                          isOpen={(isOpenByIndex || isOpenByUrl) && isFilterVisible}
                          onKeyDown={(e) => {
                            if (e.key !== 'Escape') return;
                            if (isFullscreen) return;
                            e.preventDefault();
                            e.stopPropagation();
                            setContentModerationAckIndex(null);
                            setContentModerationAckUrl(null);
                            handleSetPreviewPopoverOpen(-1);
                            setPreviewPopoverUrl(null);
                          }}
                        >
                          <PopoverTrigger>
                            <Button
                              type="button"
                              iconOnly
                              size="sm"
                              style="outline"
                              color="subdued"
                              background="default"
                              aria-label={t('chat.evidence.previewSource')}
                              aria-haspopup="dialog"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isUfdrRoot) {
                                  setUfdrPreviewContext({
                                    url,
                                    path: toProvenanceLookupPath(fileSummary?.searchablePath || url),
                                    caseFolderName: ufdrCaseFolderName,
                                    fileStatusId:
                                      node.fileStatusId ??
                                      (node.nodeType === 'file' ? node.id : null) ??
                                      fileSummary?.fileStatusId ??
                                      null,
                                    label: fileName,
                                    fileInfo: fileSummary,
                                  });
                                  handleSetPreviewPopoverOpen(-1);
                                  setPreviewPopoverUrl(null);
                                } else if (effectiveCmType) {
                                  if (index >= 0) {
                                    setContentModerationAckIndex(index);
                                  } else {
                                    setContentModerationAckUrl(url);
                                  }
                                } else if (url && useMetaViewer) {
                                  const caseParam = caseId && caseId.trim() !== '' ? `&caseId=${encodeURIComponent(caseId)}` : '';
                                  window.open(`/meta-viewer?s3Key=${encodeURIComponent(s3Key)}${caseParam}`, '_blank');
                                } else if (url && useEmailViewer) {
                                  const caseParam = caseId && caseId.trim() !== '' ? `&caseId=${encodeURIComponent(caseId)}` : '';
                                  if (
                                    mailboxPreviewTarget(themeEmailArchive) === 'shell' &&
                                    emailArchivePreview
                                  ) {
                                    emailArchivePreview.open({ s3Key });
                                  } else {
                                    window.open(`/email-viewer?s3Key=${encodeURIComponent(s3Key)}${caseParam}`, '_blank');
                                  }
                                } else if (index >= 0) {
                                  handleSetPreviewPopoverOpen(index);
                                } else if (url) {
                                  handleSetPreviewPopoverOpenForUrl(url);
                                }
                              }}
                            >
                              {effectiveCmType ? (
                                <EyeSlashIcon
                                  weight={hoveredIconKey === url ? "fill" : "regular"}
                                  className="text-red-400 transition-all duration-200"
                                  onMouseEnter={() => setHoveredIconKey(url)}
                                  onMouseLeave={() => setHoveredIconKey(null)}
                                />
                              ) : (
                                <MagnifyingGlassPlusIcon
                                  weight={hoveredIconKey === url ? "bold" : "regular"}
                                  className="text-black/70 transition-all duration-200"
                                  onMouseEnter={() => setHoveredIconKey(url)}
                                  onMouseLeave={() => setHoveredIconKey(null)}
                                />
                              )}
                            </Button>
                          </PopoverTrigger>
                          {/*
                            overflow-hidden on content so React Aria's placement
                            maxHeight on the floating root actually clips. Scrollport
                            max-h uses min(75vh, 100vh-120px, 100%) so soft-fit can
                            read a real CSS cap while still respecting the popover.
                          */}
                          <PopoverContent className={`${MEDIA_PREVIEW_OVERLAY_SURFACE_CLASS} w-full sm:w-auto max-w-[100vw] sm:max-w-[520px] h-full sm:h-auto max-h-[100vh] sm:max-h-[95vh] overflow-hidden flex flex-col p-0 rounded-md`}>
                            {isCmAckActive ? null : (
                            <div
                              ref={previewPopoverRef}
                              data-source-card-scrollport
                              data-evidence-locker-preview
                              className="z-50 w-full sm:w-auto max-w-[100vw] sm:max-w-[500px] min-h-0 max-h-[min(75vh,calc(100vh-120px),100%)] h-full sm:h-auto overflow-y-auto custom-scrollbar2"
                            >
                              {/* Two structurally distinct branches so React fully unmounts/mounts on toggle.
                                  The obfuscated preview is gated on ``canSeeCsamTags``; original reveal
                                  additionally requires ``canRevealOriginal``. When tag-level access is off we
                                  fall through to the normal SourceCard. */}
                              {(canSeeCsamTags && effectiveCmType && effectiveShowObfuscated) ? (
                                <SourceCard
                                  key="obf"
                                  sources={[{
                                    ...buildEvidenceLockerPreviewSource(url, fileSummary, pageCount),
                                    previewFilePath: effectivePreviewUrl ?? undefined,
                                    label: fileName,
                                  }]}
                                  selectedSourceIndex={0}
                                  answerIndex={0}
                                  epochTimeSecs={0}
                                  handleDownload={handleDownloadWithIntercomFlag}
                                  handleAddSourceToScope={(source) => {
                                    setPreviewPopoverOpen(-1);
                                    setPreviewPopoverUrl(null);
                                    addSourceToScope(pathForAssistantScope(source));
                                  }}
                                  handleCopySourceForTimeline={handleCopySourceForTimeline}
                                  handleCopyToMediaShare={mediaSourceShare?.addToMediaShare ? (source: Source, _index: number, onSuccess?: () => void) => mediaSourceShare.addToMediaShare(source, onSuccess) : undefined}
                                  setIsExpanded={setIsExpanded}
                                  fullScreenSource={undefined}
                                  copiedSource={copiedSource}
                                  addedSource={addedSource}
                                  closeSources={() => { handleSetPreviewPopoverOpen(-1); setPreviewPopoverUrl(null); }}
                                  addToTimeline={addToTimeline}
                                  caseId={caseId}
                                  headerPrefix={
                                    effectiveCmType ? <CsamDetectionPill type={effectiveCmType} /> : undefined
                                  }
                                  showObfuscatedPreview={effectiveShowObfuscated}
                                  onToggleObfuscatedPreview={canRevealOriginal ? setShowObfuscatedPreview : undefined}
                                  contentModerationType={effectiveCmType}
                                />
                              ) : (canSeeCsamTags && effectiveCmType && !effectiveShowObfuscated) ? (
                                <SourceCard
                                  key="orig"
                                  sources={[{
                                    ...toBypassCmPreviewSource(
                                      buildEvidenceLockerPreviewSource(url, fileSummary, pageCount),
                                    ),
                                    label: fileName,
                                  }]}
                                  selectedSourceIndex={0}
                                  answerIndex={0}
                                  epochTimeSecs={0}
                                  handleDownload={handleDownloadWithIntercomFlag}
                                  handleAddSourceToScope={(source) => {
                                    setPreviewPopoverOpen(-1);
                                    setPreviewPopoverUrl(null);
                                    addSourceToScope(pathForAssistantScope(source));
                                  }}
                                  handleCopySourceForTimeline={handleCopySourceForTimeline}
                                  handleCopyToMediaShare={mediaSourceShare?.addToMediaShare ? (source: Source, _index: number, onSuccess?: () => void) => mediaSourceShare.addToMediaShare(source, onSuccess) : undefined}
                                  setIsExpanded={setIsExpanded}
                                  fullScreenSource={undefined}
                                  copiedSource={copiedSource}
                                  addedSource={addedSource}
                                  closeSources={() => { handleSetPreviewPopoverOpen(-1); setPreviewPopoverUrl(null); }}
                                  addToTimeline={addToTimeline}
                                  caseId={caseId}
                                  headerPrefix={
                                    effectiveCmType ? <CsamDetectionPill type={effectiveCmType} /> : undefined
                                  }
                                  showObfuscatedPreview={effectiveShowObfuscated}
                                  onToggleObfuscatedPreview={canRevealOriginal ? setShowObfuscatedPreview : undefined}
                                  contentModerationType={effectiveCmType}
                                />
                              ) : (
                                <SourceCard
                                  sources={[buildEvidenceLockerPreviewSource(url, fileSummary, pageCount)]}
                                  selectedSourceIndex={0}
                                  answerIndex={0}
                                  epochTimeSecs={0}
                                  handleDownload={handleDownloadWithIntercomFlag}
                                  handleAddSourceToScope={(source) => {
                                    setPreviewPopoverOpen(-1);
                                    setPreviewPopoverUrl(null);
                                    addSourceToScope(pathForAssistantScope(source));
                                  }}
                                  handleCopySourceForTimeline={handleCopySourceForTimeline}
                                  handleCopyToMediaShare={mediaSourceShare?.addToMediaShare ? (source: Source, _index: number, onSuccess?: () => void) => mediaSourceShare.addToMediaShare(source, onSuccess) : undefined}
                                  setIsExpanded={setIsExpanded}
                                  fullScreenSource={buildEvidenceLockerPreviewSource(
                                    url,
                                    fileSummary,
                                    pageCount,
                                  )}
                                  copiedSource={copiedSource}
                                  addedSource={addedSource}
                                  closeSources={() => {
                                    handleSetPreviewPopoverOpen(-1);
                                    setPreviewPopoverUrl(null);
                                  }}
                                  addToTimeline={addToTimeline}
                                  caseId={caseId}
                                />
                              )}
                            </div>
                            )}
                          </PopoverContent>
                        </Popover>
                        {isCmAckActive && (
                          <ContentModerationAckModal
                            contentModerationType={effectiveCmType ?? ''}
                            onCancel={() => {
                              setContentModerationAckIndex(null);
                              setContentModerationAckUrl(null);
                            }}
                            onAcknowledge={() => {
                              setContentModerationAckIndex(null);
                              setContentModerationAckUrl(null);
                              if (index >= 0) {
                                handleSetPreviewPopoverOpen(index);
                              } else {
                                // Tree-only row: open by URL and keep the
                                // obfuscated preview on so the SourceCard
                                // renders the safe (blurred) version first.
                                setShowObfuscatedPreview(true);
                                handleSetPreviewPopoverOpenForUrl(url);
                              }
                            }}
                          />
                        )}
                        </>
                        )}
                      </div>
                    );
                  }}
                  renderRowNameSuffix={({ url, node }) => {
                    if (!canSeeCsamTags) return null;
                    const idx = availableEvidence.findIndex((e) => e.url === url);
                    const src = idx >= 0 ? availableEvidence[idx] : null;
                    const nodeSummary = fileSummaryFromTreeNode(node);
                    const nodeCmType =
                      nodeSummary?.contentModeration?.type &&
                      nodeSummary.contentModeration.findingStatus !== 'REVIEWED'
                        ? nodeSummary.contentModeration.type
                        : null;
                    const cmType = src?.contentModerationType ?? nodeCmType;
                    if (!cmType) return null;
                    return (
                      <div className="flex flex-row gap-1 shrink-0">
                        <CsamDetectionPill type={cmType} />
                      </div>
                    );
                  }}
                />
              </div>
        )}
        <div ref={addSourcesButtonRef} className="p-0 m-0 pt-2 mt-2 flex justify-end border-t-3 border-brand-blue-light/50 border-double">
          {(selectedEvidence?.length > 0 || selectedScopes?.length > 0 || excludedEvidence?.length > 0 || excludedScopes?.length > 0) && (
            <Button
              type="button"
              size="sm"
              style="solid"
              color="accent"
              background="inverse"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddScopesAndFiles(
                {scopesToAdd: selectedScopes, filesToAdd: selectedEvidence, excludedScopesToAdd: excludedScopes, excludedFilesToAdd: excludedEvidence}
                )
              }}
            >
              {t('chat.evidence.addSelected', {
                count: (selectedEvidence?.length || 0)
                  + (selectedScopes?.length || 0)
                  + (excludedEvidence?.length || 0)
                  + (excludedScopes?.length || 0),
              })}
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  // Keep the question box the same height as the action column beside it. That
  // column stacks 44px icon-only buttons with an 8px gap: 96px for submit +
  // DeepDive, 148px once Mobile joins them. Subtract the textarea's own
  // vertical margins (5px a side, 10px at sm and up).
  const questionBoxMinHeightClass =
    isFullyExpanded && mobileAnalysisAgentEnabled
      ? 'min-h-[138px] sm:min-h-[128px]'
      : 'min-h-[85px] sm:min-h-[72px]';

  return (
    <>
      {contentCategoriesEnabled && typeof document !== 'undefined' && isSourceCardPopoverOpen && createPortal(
        <DismissibleBlurOverlay
          position="fixed"
          zIndexClass={raisePreviewAboveModal ? 'z-[100001]' : 'z-[40]'}
          className="bg-black/50"
          onClose={() => {
            setPreviewPopoverOpen(-1);
            setPreviewPopoverUrl(null);
          }}
          closeLabel={t('chat.evidence.closePreview')}
          dismissOnBackdropClick
          showCloseButton={!hasPreviewForeground}
        />,
        document.body
      )}
      <EvidenceLockerMobileForensicOverlay
        isOpen={!!ufdrPreviewContext}
        context={ufdrPreviewContext}
        onClose={() => {
          setUfdrPreviewContext(null);
        }}
      />
    <form
      ref={formRef}
      data-question-bar-interactive
      onSubmit={(e) => handleSubmit(e, false)}
      className={`box-border flex flex-col z-[2] items-center justify-center gap-1 m-0 p-0 mb-0 transition-all duration-500 ease-in-out outline-none
                ${hasChatHistory ? `fixed ${featureFlags?.enableMediaAndSourceShare ? 'bottom-[10px] sm:bottom-[50px]' : 'bottom-0'} left-1/2 transform -translate-x-1/2` : ''}
                ${isExpanded ? 'w-11/12 sm:w-6/12' : 'w-[60px]'}
                ${!hasChatHistory && isExpanded ? 'relative' : ''}
            `}
      autoComplete='off'
    >
      {!useNewEvidenceLockerModal ? (
      <div className={`box-border flex flex-row justify-center items-center gap-2 bottom-[0px] z-50 ${!isFilterVisible || !isExpanded ? 'max-w-[1px] max-h-[1px] overflow-hidden opacity-0' : ''} ${hasChatHistory ? 'bottom-[132px]' : ''}`}>
        <div ref={evidenceLockerRef} className={`bg-white text-black rounded-lg max-w-[95vw] border-[3px] border-[#99C4F6] border-solid ${contentCategoriesEnabled ? 'w-[920px]' : 'w-[800px]'}`}>
          <div className="flex flex-row justify-between rounded-t-sm items-center text-black/90 border-b-[3px] border-[#99C4F6] border-solid bg-brand-blue">
            <label className="text-white text-base px-2 py-1 flex flex-row justify-center items-center gap-2">
              <LockersIcon size={20} weight="regular" />
              {t('chat.evidence.locker')}
            </label>
            <Button
              type="button"
              iconOnly
              size="sm"
              style="text"
              color="light"
              background="inverse"
              aria-label={t('chat.evidence.closeLocker')}
              className="mr-1"
              onClick={(e) => {
                e.stopPropagation();
                setIsFilterVisible(false);
                setPreviewPopoverOpen(-1);
                setFullscreenMediaItem(null);
              }}
            >
              <XIcon weight="regular" />
            </Button>
          </div>
          {contentCategoriesEnabled ? (
          <div ref={evidenceLockerContentRef} className="flex min-w-0 flex-col gap-4 p-4 w-full">
              <div className="flex min-w-0 max-w-full flex-row items-center justify-center gap-[10px] w-[calc(100%-32px)]">
                <div
                  className="box-border flex flex-row flex-1 justify-between items-center p-2 bg-white text-black/90 rounded-md cursor-pointer border-[2px] border-brand-blue-light border-solid bg-brand-blue-light/20 h-[36px]"
                  onClick={(e) => {
                    e.stopPropagation();
                    const input = e.currentTarget.querySelector('input');
                    input?.focus();
                  }}
                >
                  <input
                    type="text"
                    aria-label={t('chat.evidence.searchEvidenceAndScopes')}
                    className="box-border text-[14px] w-full outline-none bg-transparent placeholder:text-black/70"
                    placeholder={t('chat.evidence.searchMinimumPlaceholder')}
                    value={filesInputValue || ''}
                    onChange={(e) => { setEvidenceInputValue(e.target.value); setScopeInputValue(e.target.value) }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                  />
                  <ListMagnifyingGlassIcon size={20} />
                </div>
                <ScopeDropdown
                  selectedContentCategories={selectedContentCategories}
                  onToggleCategory={(category) => {
                    setScopesAndFiles((prev) => ({
                      ...prev,
                      ...toggleEvidenceCategoryScopeInclude(prev, category),
                    }));
                  }}
                />
              </div>
              <div className="flex min-w-0 max-w-full flex-col gap-4 pt-4 px-4 pb-2 bg-[#f3f3f3] rounded-lg w-[calc(100%-64px)] h-[110px]">
                <span className="text-[14px] text-black tracking-[-0.21px] sm:whitespace-nowrap">{t('chat.evidence.selectFileTypes')}:</span>
                <div className="filter-bar-scroll-desktop-hover min-w-0 -mx-1 touch-pan-x sm:mx-0">
                <div ref={filterBarRowRef} className="flex w-max flex-row flex-nowrap items-center gap-2 pr-1 pb-2 sm:w-full sm:min-w-0 sm:pr-0">
                  <FilterButton
                    isActive={showFolders}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleShowFolders();
                    }}
                    icon={FolderOpenIcon}
                    label={t('chat.evidence.filters.folders')}
                    surface="accent"
                    variant="expanded"
                  />
                  <div className="flex w-[3px] h-[12px] rounded-full bg-[#d9d9d9] shrink-0"></div>
                  <FilterButton
                    isActive={allFileTypes}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleAllFileTypes();
                    }}
                    icon={FilesIcon}
                    label={t('chat.evidence.filters.allFiles')}
                    surface="accent"
                    variant="expanded"
                  />
                  <div className="flex w-[3px] h-[12px] rounded-full bg-[#d9d9d9] shrink-0"></div>
                  {Object.keys(fileTypeControllers).map((key) => {
                    const Icon = fileTypeIcons[key as keyof typeof fileTypeIcons];
                    return (
                      <FilterButton
                        key={key}
                        isActive={activeFileTypes[key]}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleFileType(key);
                        }}
                        icon={Icon}
                        label={evidenceFileTypeLabel(key, t)}
                        surface="accent"
                        variant="expanded"
                      />
                    );
                  })}
                </div>
                </div>
              </div>
              {renderResultsArea("w-[calc(100%-32px)] flex flex-col gap-2")}
          </div>
          ) : (
          <div className="flex items-center p-2">
            <div className="box-border flex flex-col p-2 w-full">
              <div className="flex flex-col">
                <div className="flex flex-row justify-start items-start sm:gap-2 gap-1 text-sm mb-2">
                  <div className="flex flex-row justify-start items-center sm:gap-2 gap-1 w-full scrollbar-hide sm:overflow-x-hidden overflow-x-auto">
                    <FilterButton
                      isActive={showFolders}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleShowFolders();
                      }}
                      icon={FolderOpenIcon}
                      label={t('chat.evidence.filters.folders')}
                    surface="accent"
                    />
                    <div className="flex w-[1px] sm:w-[3px] h-[12px] rounded-full bg-black/15"></div>
                    <div className="flex flex-row items-center sm:gap-2 gap-1">
                      {Object.keys(fileTypeControllers).map((key) => {
                        const Icon = fileTypeIcons[key as keyof typeof fileTypeIcons];
                        return (
                        <FilterButton
                          key={key}
                          isActive={activeFileTypes[key] || selectedContentCategories.includes(key)}
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleFileType(key);
                          }}
                          icon={Icon}
                          label={evidenceFileTypeLabel(key, t)}
                        surface="accent"
                        />
                        );
                      })}
                      <div className="flex w-[3px] h-[12px] rounded-full bg-black/15"></div>
                      <FilterButton
                        isActive={allFileTypes}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleAllFileTypes();
                        }}
                        icon={FilesIcon}
                        label={t('chat.evidence.filters.allFiles')}
                    surface="accent"
                      />
                    </div>
                  </div>
                </div>
                <div
                  className="box-border flex flex-row justify-between items-center p-2 bg-white text-black/90 rounded-md cursor-pointer border-[2px] border-brand-blue-light border-solid bg-brand-blue-light/20 w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    const input = e.currentTarget.querySelector('input');
                    input?.focus();
                  }}
                >
                  <input
                    type="text"
                    aria-label={t('chat.evidence.searchEvidenceAndScopes')}
                    className="box-border text-[14px] w-full outline-none bg-transparent placeholder:text-black/70"
                    placeholder={t('chat.evidence.searchFileOrFolder')}
                    value={filesInputValue || ''}
                    onChange={(e) => { setEvidenceInputValue(e.target.value); setScopeInputValue(e.target.value) }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                  />
                  <ListMagnifyingGlassIcon size={20} />
                </div>
              </div>
              {renderResultsArea("w-full border border-white/20 rounded-sm mt-3")}
            </div>
          </div>
          )}
        </div>
      </div>
      ) : null}
      <motion.div
        transition={{ duration: 0.5 }}
        className={`box-border rounded-[32px] flex w-full shadow-lg border-[1px]
          border-solid border-white/50 outline-none bg-[#2d8dfe] relative
          ${isExpanded
            ? 'min-h-[60px] max-h-[200px] flex-row items-start justify-between p-2 sm:pl-2'
            : 'h-[60px] min-h-[60px] max-h-[60px] flex-row items-center justify-center p-0 overflow-hidden'}
          ${barInteractionLocked ? 'pointer-events-none' : ''}
          ${!isExpanded && !barInteractionLocked ? 'cursor-pointer' : ''}`}
        onClick={(e) => {
          if (barInteractionLocked) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          if (!isExpanded) {
            e.preventDefault();
            e.stopPropagation();
            expandQuestionBar();
            return;
          }
          const target = e.target as HTMLElement;
          if (
            target.closest(
              '#evidence-filter-picker-root, [data-evidence-filter-picker-menu], [data-testid="open-evidence-locker"]',
            )
          ) {
            return;
          }
          const textareaValue = textareaRef.current?.value?.trim() || '';
          if (textareaValue) {
            e.stopPropagation();
          }
          textareaRef.current?.focus();
          setIsFilterVisible(false);
          setFilterPickerOpen(false);
          setPreviewPopoverOpen(-1);
          setFullscreenMediaItem(null);
        }}
      >
        {isExpanded && (<motion.div className="pointer-events-auto flex shrink-0 flex-col items-left justify-left gap-2 overflow-visible">
          {useNewEvidenceLockerModal ? (
              <EvidenceFilterPickerNew
                isOpen={filterPickerOpen}
                onOpenChange={setFilterPickerOpen}
                onOpenEvidenceLocker={() => {
                  setFilterPickerOpen(false);
                  setPreviewPopoverOpen(-1);
                  setIsEvidenceLockerModalOpen(true);
                  setIsExpanded(true);
                }}
                onClearAllFilters={clearAllEvidenceFilters}
              >
                <TpTooltip
                  disabled={barInteractionLocked || lockerUiOpen || filterPickerOpen}
                  content={
                    evidenceFilterActive
                      ? t('chat.evidence.filterActiveHelp')
                      : t('chat.evidence.filterHelp')
                  }
                  placement="left"
                  delayDuration={700}
                  sideOffset={4}
                >
                  <Button
                    type="button"
                    iconOnly
                    size="lg"
                    style={(lockerUiOpen || filterPickerOpen) ? 'solid' : 'outline'}
                    color={(lockerUiOpen || filterPickerOpen) ? 'accent' : 'light'}
                    background={QUESTION_BAR_ICON_BUTTON_BACKGROUND}
                    className={`${QUESTION_BAR_ICON_BUTTON_CLASS} ${!isExpanded ? 'hidden' : ''}`}
                    data-testid="open-evidence-locker"
                    aria-label={
                      lockerUiOpen
                        ? t('chat.evidence.closeLocker')
                        : filterPickerOpen
                          ? t('chat.evidence.closeFilterMenu')
                          : t('chat.evidence.openFilterMenu')
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={handleNewModalFunnelClick}
                  >
                    {evidenceFilterActive ? (
                      <FunnelXIcon />
                    ) : (
                      <FunnelIcon />
                    )}
                  </Button>
                </TpTooltip>
              </EvidenceFilterPickerNew>
            ) : (
              <TpTooltip
                disabled={barInteractionLocked}
                content={
                  (scopesAndFiles?.files && scopesAndFiles?.files.length > 0) ||
                  (scopesAndFiles?.scopes && scopesAndFiles?.scopes.length > 0) ||
                  (scopesAndFiles?.excludedFiles &&
                  scopesAndFiles?.excludedFiles.length > 0) ||
                  (scopesAndFiles?.excludedScopes &&
                  scopesAndFiles?.excludedScopes.length > 0)
                  ? t('chat.evidence.legacyFilterActiveHelp')
                  : t('chat.evidence.selectHelp')
                }
                placement="left"
                delayDuration={700}
                sideOffset={4}
              >
              <Button
                type="button"
                iconOnly
                size="lg"
                style={lockerUiOpen ? 'solid' : 'outline'}
                color={lockerUiOpen ? 'accent' : 'light'}
                background={QUESTION_BAR_ICON_BUTTON_BACKGROUND}
                className={`${QUESTION_BAR_ICON_BUTTON_CLASS} ${!isExpanded ? 'hidden' : ''}`}
                data-testid="open-evidence-locker"
                aria-label={
                  lockerUiOpen ? t('chat.evidence.closeFilter') : t('chat.evidence.openFilter')
                }
                onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const {files, scopes} = scopesAndFiles || {files: [], scopes: []};
                const hasSelection =
                  (files && files.length > 0) || (scopes && scopes.length > 0);

                const clearEvidenceSelection = () => {
                  setScopesAndFiles({
                    scopes: [],
                    files: [],
                    excludedScopes: [],
                    excludedFiles: [],
                    contentCategories: [],
                    excludedContentCategories: [],
                    evidenceCategoryChips: [],
                    excludedEvidenceCategoryScopes: [],
                    evidenceCategoryScopes: [],
                    evidenceAllFileTypes: false,
                  });
                  if (featureFlags?.contentCategories) {
                    setActiveFileTypes({});
                    setAllFileTypes(false);
                  }
                  onAssistantEvidenceFilterCleared?.();
                  setPreviewPopoverOpen(-1);
                  setFullscreenMediaItem(null);
                };

                if (hasSelection) {
                  if (isFilterVisible) {
                    clearEvidenceSelection();
                    setIsFilterVisible(false);
                  } else {
                    setIsFilterVisible(true);
                  }
                } else {
                  const newIsFilterVisible = !isFilterVisible;
                  if (newIsFilterVisible) {
                    setPreviewPopoverOpen(-1);
                  }
                  setIsFilterVisible(newIsFilterVisible);
                }
              }}>
              {(scopesAndFiles?.files && scopesAndFiles?.files.length > 0)
                || (scopesAndFiles?.scopes && scopesAndFiles?.scopes.length > 0) ? (
                <FunnelXIcon />
              ) : (
                <FunnelIcon />
              )}
              {/* <div className="text-xs whitespace-nowrap">Scope</div> */}
            </Button>
              </TpTooltip>
            )}
          {isPromptFlagOn(featureFlags?.promptLibrary) && isPromptFlagOn(featureFlagsProp?.promptLibrary) && (
            <div
              onPointerEnter={() => setSuppressPromptLibraryTooltip(false)}
            >
            <TpTooltip
              disabled={
                barInteractionLocked || isPromptLibraryOpen || suppressPromptLibraryTooltip
              }
              content={t('chat.composer.promptSuggestions')}
              placement="left"
              delayDuration={700}
              sideOffset={4}
            >
              <div>
                <PromptLibrary
                  featureFlags={featureFlagsProp}
                  onPromptSelect={handlePromptSelect}
                  onOpenChange={handlePromptLibraryOpenChange}
                  isOpen={isPromptLibraryOpen}
                  popoverRef={promptLibraryRef}
                  triggerElement={
                    <Button
                      type="button"
                      iconOnly
                      size="lg"
                      style={isPromptLibraryOpen ? 'solid' : 'outline'}
                      color={isPromptLibraryOpen ? 'accent' : 'light'}
                      background={QUESTION_BAR_ICON_BUTTON_BACKGROUND}
                      id="prompt-library-open-button"
                      aria-label={t(isPromptLibraryOpen
                        ? 'chat.composer.closePromptLibrary'
                        : 'chat.composer.openPromptLibrary')}
                      aria-pressed={isPromptLibraryOpen}
                      className={`${QUESTION_BAR_ICON_BUTTON_CLASS} ${isExpanded ? 'flex' : 'hidden'}`}>
                      <LightbulbFilamentIcon />
                    </Button>
                  }
                />
              </div>
            </TpTooltip>
            </div>
          )}
        </motion.div>)}
        <motion.div
          animate={{ width: isExpanded ? '100%' : '0px' }}
          transition={{
            width: { duration: 0.3 },
            opacity: { duration: 0.2, delay: isExpanded ? 0 : 0.2 },
          }}
          className={`box-border flex flex-row items-start justify-start overflow-hidden ${
            isExpanded
              ? 'min-w-0 flex-1 opacity-100'
              : 'w-0 min-w-0 max-w-0 shrink-0 opacity-0 pointer-events-none'
          } ${isFullyExpanded ? 'mx-2 border-1 border-solid border-white/20 bg-white/5 rounded-[20px]' : ''}`}
        >
          <textarea
            ref={textareaRef}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={
              isSmallScreen ? '' : (isExpanded && submitUiLocked && !holdSubmitVisualLock ? t('chat.composer.loadingAnswers') : (!isExpanded && hasChatHistory ? '' : (
                isFullyExpanded ? (
                  (scopesAndFiles?.files && scopesAndFiles?.files.length > 0) ||
                  (scopesAndFiles?.scopes && scopesAndFiles?.scopes.length > 0) ||
                  (scopesAndFiles?.excludedFiles && scopesAndFiles?.excludedFiles.length > 0) ||
                  (scopesAndFiles?.excludedScopes && scopesAndFiles?.excludedScopes.length > 0) ?
                    t('chat.composer.placeholderScoped') :
                    t('chat.composer.placeholder')) :
                  ''
              )))
            }
            aria-label={t('chat.composer.ariaLabel')}
            aria-hidden={!isExpanded}
            // Free-text chat box — never a credential field. Without these,
            // the field defaults to autocomplete="on" and browsers' one-time-
            // code heuristic offers to autofill stray verification codes from
            // the user's Mail/Messages into it ("Fill code 845455 — From Mail").
            // We don't send OTPs; this stops the misfire. (Safari may still
            // ignore autocomplete="off" for OTP fields — known WebKit behavior.)
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={true}
            data-1p-ignore
            data-lpignore="true"
            tabIndex={isExpanded ? 0 : -1}
            className={`box-border flex place-content-center leading-[22px] sm:leading-[24px] bg-transparent text-[16px] font-inter font-light placeholder-white focus:placeholder-opacity-0 resize-none focus:outline-none text-white caret-white transition-opacity duration-300 items-center ${
              isExpanded
                ? `w-[calc(100%-30px)] sm:mx-[20px] mx-[5px] sm:my-[10px] my-[5px] max-h-[150px] ${questionBoxMinHeightClass} sm:max-h-[160px] custom-scrollbar2 overflow-hidden sm:overflow-auto opacity-100 visible`
                : 'w-0 h-0 min-h-0 max-h-0 m-0 p-0 opacity-0 invisible overflow-hidden pointer-events-none scrollbar-hide'
            } ${submitUiLocked && !holdSubmitVisualLock ? 'placeholder:animate-pulse' : ''}`}
            onKeyDown={handleKeyDown}
            onCopy={handleTextareaCopy}
            onPaste={handleTextareaPaste}
            readOnly={holdSubmitVisualLock}
            disabled={false}
            rows={1}
          />
        </motion.div>
        <div className={`flex flex-col gap-2 items-center justify-center ${isFullyExpanded ? 'self-start' : 'self-center mt-[1px]'}`}>
          <TpTooltip
            disabled={barInteractionLocked || !isExpanded || !filterIsTooBig}
            content={t('chat.composer.tooManyItemsHelp')}
            delayDuration={0}
            sideOffset={0}
          >
            <Button
              data-testid="AssistantQuery"
              aria-label={
                !isExpanded
                  ? t('chat.composer.expand')
                  : submitUiLocked
                    ? t('chat.composer.submitting')
                    : filterIsTooBig
                      ? t('chat.composer.tooManyItems')
                      : t('chat.composer.submit')
              }
              ref={assistantQueryButtonRef}
              type={isExpanded ? 'submit' : 'button'}
              iconOnly
              size="lg"
              style="outline"
              color="light"
              background={QUESTION_BAR_ICON_BUTTON_BACKGROUND}
              isLoading={submitUiLocked}
              leadingIcon={<MagnifyingGlassIcon />}
              className={`${QUESTION_BAR_ICON_BUTTON_CLASS} ${!isExpanded ? QUESTION_BAR_SUBMIT_RINGLESS_CLASS : ''}`}
              disabled={(isExpanded && (submitUiLocked || filterIsTooBig)) || barInteractionLocked}
              onClick={(event) => {
                event.stopPropagation();
                if (barInteractionLocked) {
                  return;
                }
                if (!isExpanded) {
                  expandQuestionBar();
                  return;
                }
                recordStat(
                  'AssistantQueryButtonClicked',
                  event,
                  statsContext,
                );
              }}
            />
          </TpTooltip>
          {isFullyExpanded && (
            <>
            <TpTooltip
              disabled={barInteractionLocked}
              content={
                <div className="flex max-w-[11rem] flex-col items-start gap-1">
                  <span className="font-medium">{t('chat.composer.deepDive')}</span>
                  <span className="text-xs text-[var(--text-muted)]">
                    {t('chat.composer.deepDiveHelp')}
                  </span>
                </div>
              }
              placement="right"
              delayDuration={700}
              sideOffset={4}
            >
              <Button
                type="button"
                iconOnly
                size="lg"
                style="outline"
                color={strandsModeQuerySent ? "success" : "light"}
                background={QUESTION_BAR_ICON_BUTTON_BACKGROUND}
                aria-label={t(strandsModeQuerySent
                  ? 'chat.composer.deepDiveSent'
                  : 'chat.composer.sendDeepDive')}
                data-testid="AssistantDeepDiveQuery"
                className={QUESTION_BAR_ICON_BUTTON_CLASS}
                disabled={barInteractionLocked || submitUiLocked || filterIsTooBig}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit(e as unknown as React.FormEvent, true, false);
                }}
              >
                {strandsModeQuerySent ? (
                  <CheckIcon weight="bold" />
                ) : (
                  <CrosshairIcon weight="regular" />
                )}
              </Button>
            </TpTooltip>
            {mobileAnalysisAgentEnabled && (
              <TpTooltip
                disabled={barInteractionLocked}
                content={
                  <div className="flex max-w-[11rem] flex-col items-start gap-1">
                    <span className="font-medium">{t('chat.composer.mobile')}</span>
                    <span className="text-xs text-[var(--text-muted)]">
                      {t('chat.composer.mobileHelp')}
                    </span>
                  </div>
                }
                placement="right"
                delayDuration={700}
                sideOffset={4}
              >
                <Button
                  type="button"
                  iconOnly
                  size="lg"
                  style="outline"
                  color={mobileModeQuerySent ? "success" : "light"}
                  background={QUESTION_BAR_ICON_BUTTON_BACKGROUND}
                  aria-label={t(mobileModeQuerySent
                    ? 'chat.composer.mobileSent'
                    : 'chat.composer.sendMobile')}
                  data-testid="AssistantMobileQuery"
                  className={QUESTION_BAR_ICON_BUTTON_CLASS}
                  disabled={barInteractionLocked || submitUiLocked || filterIsTooBig}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit(e as unknown as React.FormEvent, false, true);
                  }}
                >
                  {mobileModeQuerySent ? (
                    <CheckIcon weight="bold" />
                  ) : (
                    <DeviceMobileIcon weight="regular" />
                  )}
                </Button>
              </TpTooltip>
            )}
            </>
          )}
        </div>
      </motion.div>
      {(
        (scopesAndFiles?.files && scopesAndFiles?.files.length > 0)
        || (scopesAndFiles?.scopes && scopesAndFiles?.scopes.length > 0)
        || (scopesAndFiles?.excludedFiles && scopesAndFiles?.excludedFiles.length > 0)
        || (scopesAndFiles?.excludedScopes && scopesAndFiles?.excludedScopes.length > 0)
        || (contentCategoriesEnabled && selectedContentCategories && selectedContentCategories.length > 0)
        || (contentCategoriesEnabled && excludedContentCategories.length > 0)
      ) && isExpanded && (
        <div
          ref={selectedItemsRef}
          className="box-border bg-[#99C4F6] rounded-b-3xl w-full px-2 pb-2 pt-[28px] mt-[-28px]"
          style={{ width: formRef.current?.offsetWidth }}
        >
          <div ref={barRef} className="max-h-[142px] overflow-y-auto custom-scrollbar2 box-border flex flex-wrap items-center justify-start gap-2 mt-[-4px] pt-2">
            {contentCategoriesEnabled && excludedContentCategories.length > 0 && excludedContentCategories.map((fileType, index) => {
              const Icon = fileTypeIcons[fileType as keyof typeof fileTypeIcons];
              return (
                <div
                  key={`excluded-filetype-${index}`}
                  className="flex flex-row justify-center items-center gap-1 rounded-full pl-1 pr-3 pt-[3px] pb-0 text-white/40 border-[1px] border-solid border-red-600/50 hover:bg-red-600/70 bg-red-600/10"
                >
                  <Button
                    type="button"
                    iconOnly
                    size="sm"
                    style="text"
                    color="light"
                    background="inverse"
                    aria-label={t('chat.evidence.removeCategory', {
                      type: evidenceFileTypeLabel(fileType, t),
                    })}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeExcludedContentCategory(fileType);
                    }}
                    className="hover:text-black transition-colors duration-200 cursor-pointer"
                  >
                    <XCircleIcon weight="regular" className="mt-[-2px]" />
                  </Button>
                  <div className="flex flex-row items-center justify-center gap-1 cursor-default">
                    <Icon size={18} weight="regular" className="text-white/80" />
                    <label className="text-white/100 text-sm pb-[2.5px]">
                      {fileType}
                    </label>
                  </div>
                </div>
              );
            })}
            {contentCategoriesEnabled && selectedContentCategories.length > 0 && selectedContentCategories.map((fileType, index) => {
              const Icon = fileTypeIcons[fileType as keyof typeof fileTypeIcons];
              return (
                <div
                  key={`filetype-${index}`}
                  className="flex flex-row justify-center items-center gap-1 rounded-full pl-1 pr-3 pt-[3px] pb-0 text-white/40 border-[1px] border-solid border-brand-blue/70 hover:bg-brand-blue/70 bg-brand-blue/20"
                >
                  <Button
                    type="button"
                    iconOnly
                    size="sm"
                    style="text"
                    color="light"
                    background="inverse"
                    aria-label={t('chat.evidence.removeCategory', {
                      type: evidenceFileTypeLabel(fileType, t),
                    })}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeContentCategory(fileType);
                    }}
                    className="hover:text-black transition-colors duration-200 cursor-pointer"
                  >
                    <XCircleIcon weight="regular" className="mt-[-2px]" />
                  </Button>
                  <div className="flex flex-row items-center justify-center gap-1 cursor-default">
                    <Icon size={18} weight="regular" className="text-white/80" />
                    <label className="text-white/100 text-sm pb-[2.5px]">
                      {fileType}
                    </label>
                  </div>
                </div>
              );
            })}
            {formRef.current && formRef.current !== null && [
              ...([...(scopesAndFiles?.scopes ?? []), ...(scopesAndFiles?.excludedScopes ?? [])].sort((a: string, b: string) =>
                resolveScopeDisplay(a).localeCompare(resolveScopeDisplay(b), undefined, { sensitivity: 'base' }),
              )),
              ...([...(scopesAndFiles?.files ?? []), ...(scopesAndFiles?.excludedFiles ?? [])].sort((a: string, b: string) => a.localeCompare(b))),
            ].map((fileOrScope, index) => {
              const isScopeChip =
                scopesAndFiles?.scopes?.includes(fileOrScope) ||
                scopesAndFiles?.excludedScopes?.includes(fileOrScope);
              return (
                <div
                  key={index}
                  className={`
                    flex flex-row justify-center items-center gap-1 rounded-full pl-1 pr-3 pt-[3px] pb-0 text-white/40
                    border-[1px] border-solid relative
                    ${
                      (scopesAndFiles?.excludedFiles?.includes(fileOrScope) || scopesAndFiles?.excludedScopes?.includes(fileOrScope))
                        ? 'border-red-600/50 hover:bg-red-600/70 bg-red-600/10'
                        : 'border-[#2d8dfe]/50 hover:bg-[#2d8dfe]/70'
                    }
                  `}
                  onMouseEnter={() => setHoveredItem(fileOrScope)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <Button
                    type="button"
                    iconOnly
                    size="sm"
                    style="text"
                    color="light"
                    background="inverse"
                    aria-label={t(
                      isScopeChip ? 'chat.evidence.removeScope' : 'chat.evidence.removeFile',
                      {
                        name: isScopeChip
                          ? resolveScopeDisplay(fileOrScope)
                          : removeMD5Hash(fileOrScope.split('/').pop() || ''),
                      },
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeScopeOrFile(fileOrScope);
                    }}
                    className="hover:text-black transition-colors duration-200 cursor-pointer"
                  >
                    <XCircleIcon weight="regular" className="mt-[-2px]" />
                  </Button>
                  <div className="flex flex-row items-center justify-center gap-1 cursor-default">
                    {scopesAndFiles?.scopes?.includes(fileOrScope) && (
                      <FolderOpenIcon size={18} weight="regular" className="text-white/80" />
                    )}
                    <label className="text-white/100 text-sm pb-[2.5px]">
                      {isScopeChip
                        ? resolveScopeDisplay(fileOrScope)
                        : removeMD5Hash(fileOrScope.split('/').pop() || '')}
                    </label>
                    {hoveredItem === fileOrScope && getHoverButtonStyle(fileOrScope)}
                  </div>
                </div>
            );
            })}
          </div>
        </div>
      )}
      {hasChatHistory && (
        <>
          <div className="flex flex-row items-center justify-center whitespace-nowrap">
            <Button
              type="button"
              style="solid"
              color="secondary"
              background="inverse"
              leadingIcon={<ArrowClockwiseIcon />}
              className="my-3"
              onClick={() => {
                setFullscreenMediaItem(null);
                setPreviewPopoverOpen(-1);
                setIsFilterVisible(false);
                setIsAlertOpen(true);
              }}
            >
              {t('chat.reset.startNew')}
            </Button>
          </div>
          <Modal
            id="start-new-session-confirm-modal"
            isOpen={isAlertOpen}
            onOpenChange={(open) => {
              if (!open) {
                setIsAlertOpen(false);
              }
            }}
            title={t('chat.reset.confirmTitle')}
            size="sm"
            showCloseButton={false}
            footer={
              <div className="flex w-full flex-row items-center justify-between gap-2">
                <Button
                  id="CancelReset"
                  title={t('chat.reset.cancel')}
                  style="text"
                  color="light"
                  background="inverse"
                  onClick={(event) => {
                    event.stopPropagation();
                    setIsAlertOpen(false);
                    recordStat('CancelResetButtonClicked', event, statsContext);
                  }}
                >
                  {t('chat.reset.cancel')}
                </Button>
                <Button
                  id="ResetConversationHistory"
                  title={t('chat.reset.resetTitle')}
                  style="solid"
                  color="accent"
                  background="inverse"
                  disabled={isResetting}
                  isLoading={isResetting}
                  loadingText={
                    <>
                      {t('chat.reset.starting')}
                      <span className="flex">
                        <span className="animate-[bounce_1s_infinite_0ms]">.</span>
                        <span className="animate-[bounce_1s_infinite_200ms]">.</span>
                        <span className="animate-[bounce_1s_infinite_400ms]">.</span>
                      </span>
                    </>
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    onClearThread();
                    setEvidenceInputValue('');
                    recordStat('ResetConversationHistoryButtonClicked', event, statsContext);
                  }}
                >
                  {t('chat.reset.startNew')}
                </Button>
              </div>
            }
          >
            <p className="m-0 italic text-white/90">
              {t('chat.reset.confirmMessage')}
            </p>
          </Modal>
        </>
      )}
    </form>
      {useNewEvidenceLockerModal ? (
        <EvidenceLockerModalNew
          isOpen={isEvidenceLockerModalOpen}
          onClose={() => setIsEvidenceLockerModalOpen(false)}
          addToTimeline={addToTimeline}
        />
      ) : null}
    </>
  );
};
